import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  X, Loader2, TrendingUp, Star, Trophy, Activity, Sparkles,
  IndianRupee, MessageSquare, Layers, ArrowUpRight, ExternalLink,
  Search, ChevronDown, ChevronUp, Package, ArrowUp, ArrowDown, Minus, Download, Info, Medal, Crown,
  Pencil, Save, RefreshCw, Leaf, ShieldCheck, Upload, FileDown, Plus, Sliders,
  Columns3, Eye, EyeOff,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, CartesianGrid, ZAxis,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Formatting helpers ─────────────────────────────────────────────
// Currency/locale config per Amazon marketplace.
const MARKETPLACES = {
  IN: { code: "IN", flag: "🇮🇳", label: "India",  symbol: "₹", currency: "INR",
        bigStep: { value: 1e5, suffix: "L" } }, // Indian Lakh
  US: { code: "US", flag: "🇺🇸", label: "US",     symbol: "$", currency: "USD",
        bigStep: { value: 1e6, suffix: "M" } },
  UK: { code: "UK", flag: "🇬🇧", label: "UK",     symbol: "£", currency: "GBP",
        bigStep: { value: 1e6, suffix: "M" } },
  DE: { code: "DE", flag: "🇩🇪", label: "Germany",symbol: "€", currency: "EUR",
        bigStep: { value: 1e6, suffix: "M" } },
  CA: { code: "CA", flag: "🇨🇦", label: "Canada", symbol: "C$", currency: "CAD",
        bigStep: { value: 1e6, suffix: "M" } },
  AU: { code: "AU", flag: "🇦🇺", label: "Australia", symbol: "A$", currency: "AUD",
        bigStep: { value: 1e6, suffix: "M" } },
  AE: { code: "AE", flag: "🇦🇪", label: "UAE",    symbol: "AED", currency: "AED",
        bigStep: { value: 1e6, suffix: "M" } },
};
const getMarketplace = (code) => MARKETPLACES[(code || "IN").toUpperCase()] || MARKETPLACES.IN;
const fmtMoney = (n, marketplace = "IN") => {
  if (n === null || n === undefined) return "—";
  const mp = getMarketplace(marketplace);
  const big = mp.bigStep;
  if (n >= big.value) return `${mp.symbol}${(n / big.value).toFixed(1)}${big.suffix}`;
  if (n >= 1e3 && mp.code !== "IN") return `${mp.symbol}${(n / 1e3).toFixed(1)}k`;
  if (n >= 1e3 && mp.code === "IN") return `${mp.symbol}${(n / 1e3).toFixed(1)}k`;
  // For non-INR markets, show decimals (e.g. $12.99 vs ₹13).
  if (mp.code === "IN") return `${mp.symbol}${Math.round(n)}`;
  return `${mp.symbol}${n.toFixed(2)}`;
};
// Backwards-compat: legacy fmtINR always renders INR.
const fmtINR = (n) => fmtMoney(n, "IN");
const fmtCount = (n) => {
  if (n === null || n === undefined) return "—";
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return String(n);
};
const hhiLabel = (h) => {
  if (h < 1500) return { label: "Fragmented", tone: "text-emerald-700" };
  if (h < 2500) return { label: "Moderate", tone: "text-amber-700" };
  return { label: "Concentrated", tone: "text-red-700" };
};
// Top-3 concentration (a.k.a. CR3) — combined market share of the 3 largest brands.
// Simpler & more intuitive than HHI for general users.
const top3Label = (pct) => {
  if (pct < 40) return { label: "Fragmented", tone: "text-emerald-700", dot: "bg-emerald-400", narrative: "no single brand dominates and the field is wide open." };
  if (pct < 70) return { label: "Balanced",   tone: "text-amber-700",   dot: "bg-amber-400",   narrative: "a handful of brands hold meaningful share but no monopoly." };
  return        { label: "Concentrated", tone: "text-red-700", dot: "bg-red-400", narrative: "one or two players dominate the category." };
};

// ─── Scroll-reveal section wrapper ──────────────────────────────────
const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            setTimeout(() => setShown(true), delay);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.08, rootMargin: "-40px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
    >
      {children}
    </div>
  );
};

// ─── Story-style section shell ──────────────────────────────────────
const StorySection = ({ id, eyebrow, title, blurb, accent = "slate", bg = "bg-white", children }) => {
  const accentMap = {
    slate:   "text-slate-500",
    indigo:  "text-indigo-600",
    emerald: "text-emerald-600",
    amber:   "text-amber-600",
    pink:    "text-pink-600",
    violet:  "text-violet-600",
  };
  const accentBar = {
    slate:   "from-slate-300 to-slate-500",
    indigo:  "from-indigo-400 to-violet-500",
    emerald: "from-emerald-400 to-teal-500",
    amber:   "from-amber-400 to-orange-500",
    pink:    "from-pink-400 to-rose-500",
    violet:  "from-violet-400 to-fuchsia-500",
  };
  return (
    <section id={id} className={`${bg} px-6 sm:px-12 py-14 sm:py-20 scroll-mt-24`}>
      <Reveal>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <div className={`h-px w-12 bg-gradient-to-r ${accentBar[accent]}`} />
            <span className={`text-[10px] font-bold uppercase tracking-[0.3em] ${accentMap[accent]}`}>{eyebrow}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-[1.1]">
            {title}
          </h2>
          {blurb && <p className="text-sm text-slate-500 mt-3 max-w-2xl">{blurb}</p>}
          <div className="mt-10">{children}</div>
        </div>
      </Reveal>
    </section>
  );
};

// ─── Giant hero number ──────────────────────────────────────────────
const HeroStat = ({ label, value, sub, accent = "text-slate-900" }) => (
  <div className="space-y-1 min-w-0">
    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">{label}</div>
    <div className={`text-4xl sm:text-5xl font-bold tabular-nums leading-none ${accent} font-mono whitespace-nowrap`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1 truncate">{sub}</div>}
  </div>
);

// ─── Brand Share Grid (crisp HTML replacement for Recharts Treemap) ─
// SVG <text> inside Recharts Treemap rendered blurry under html2canvas /
// PDF export. This pure-HTML/CSS version sizes tiles via `flex-grow`
// (proportional to brand listing count) so text stays sharp at any DPI.
const BRAND_PALETTE = [
  "#4f46e5", "#7c3aed", "#0ea5e9", "#059669",
  "#d97706", "#dc2626", "#db2777", "#0d9488",
  "#2563eb", "#9333ea", "#0891b2", "#65a30d",
];

const BrandShareGrid = ({ brands, onBrandClick, onEditBrandAlias }) => {
  if (!brands?.length) {
    return <div className="text-xs text-slate-400 py-16 text-center">No brand data captured.</div>;
  }

  const top = brands.slice(0, 12);
  const tail = brands.slice(12);
  const tailCount = tail.reduce((s, b) => s + b.count, 0);
  const tailShare = Number(tail.reduce((s, b) => s + (b.share_pct || 0), 0).toFixed(1));

  const all = [...top];
  if (tail.length) {
    all.push({ brand: `+${tail.length} more brands`, count: tailCount, share_pct: tailShare, _tail: true });
  }

  // Split into two visual rows. Top row gets the largest tiles.
  const splitAt = Math.min(4, Math.max(1, Math.ceil(all.length / 2)));
  const row1 = all.slice(0, splitAt);
  const row2 = all.slice(splitAt);
  const row1Total = row1.reduce((s, b) => s + b.count, 0) || 1;
  const row2Total = row2.reduce((s, b) => s + b.count, 0) || 1;
  const grand = row1Total + row2Total;
  const row1HeightPct = row2.length ? Math.min(70, Math.max(50, Math.round((row1Total / grand) * 100))) : 100;
  const row2HeightPct = 100 - row1HeightPct;

  const tile = (b, paletteIdx, isBig) => (
    <div
      key={b.brand}
      role={!b._tail && onBrandClick ? "button" : undefined}
      tabIndex={!b._tail && onBrandClick ? 0 : undefined}
      onClick={!b._tail && onBrandClick ? () => onBrandClick(b) : undefined}
      onKeyDown={!b._tail && onBrandClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onBrandClick(b); } } : undefined}
      className={`group relative overflow-hidden p-3 flex flex-col justify-between text-white transition-all duration-200 hover:z-10 shadow-sm hover:shadow-lg ${
        !b._tail && onBrandClick ? "cursor-pointer hover:scale-[1.04] hover:ring-2 hover:ring-white/60" : "cursor-default"
      }`}
      style={{
        flexGrow: Math.max(1, b.count),
        flexBasis: 0,
        minWidth: 72,
        backgroundColor: b._tail ? "#64748b" : BRAND_PALETTE[paletteIdx % BRAND_PALETTE.length],
      }}
      title={`${b.brand} · ${b.count} listings · ${b.share_pct}% share${!b._tail && onBrandClick ? ' · click to view ASINs' : ''}`}
      data-testid={`brand-tile-${b.brand}`}
    >
      <div className={`font-bold leading-tight truncate ${isBig ? 'text-[14px]' : 'text-[12px]'}`}>
        {b.brand}
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <span className={`font-mono tabular-nums font-bold leading-none ${isBig ? 'text-[22px]' : 'text-[16px]'}`}>
          {b.share_pct}%
        </span>
        <span className="font-mono tabular-nums text-[10px] opacity-80 whitespace-nowrap">
          {b.count} {b.count === 1 ? 'item' : 'items'}
        </span>
      </div>
      {/* Inline pencil — hover-revealed; standardize this brand name */}
      {!b._tail && onEditBrandAlias && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEditBrandAlias(b.brand); }}
          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-md inline-flex items-center justify-center text-white/70 hover:text-white bg-black/0 hover:bg-black/25 opacity-0 group-hover:opacity-100 transition-all"
          title={`Standardize "${b.brand}"`}
          data-testid={`brand-tile-alias-edit-${b.brand}`}
        >
          <Pencil className="w-3 h-3" />
        </button>
      )}
    </div>
  );

  // Submerged look — zero gap between tiles, tiles share edges
  return (
    <div className="flex flex-col gap-0" style={{ height: 340 }}>
      <div className="flex gap-0" style={{ height: `${row1HeightPct}%` }}>
        {row1.map((b, i) => tile(b, i, true))}
      </div>
      {row2.length > 0 && (
        <div className="flex gap-0" style={{ height: `${row2HeightPct}%` }}>
          {row2.map((b, i) => tile(b, i + row1.length, false))}
        </div>
      )}
    </div>
  );
};

// ─── Price Positioning Quadrant ─────────────────────────────────────
// ─── Price Positioning Quadrant (true 2×2 matrix) ──────────────────
// Classifies each brand into one of four price zones, then lays them
// out in a 2×2 grid matching a classic strategic-quadrant aesthetic:
//   ┌───────────────────┬──────────────────┐
//   │ Upper Mid         │ Premium          │ ← higher tier on top
//   │ Q2–Q3             │ ≥ Q3             │
//   ├───────────────────┼──────────────────┤
//   │ Budget Friendly   │ Mid Value        │
//   │ < Q1              │ Q1–Q2            │
//   └───────────────────┴──────────────────┘
// Each cell shows the top-5 brands in that band with their avg price,
// ranked by listing count (or units sold when backend provides it).
const PriceQuadrant = ({ products, marketplace = "IN", onZoneClick, onEditPackAlias }) => {
  // Derive available pack sizes for the filter chips — restricted to the
  // pack sizes seen on the TOP 10 BESTSELLER listings (by rank, ascending).
  // Rationale: the long tail of a category has erratic pack sizes that
  // dilute the comparison; the user only cares about what's actually
  // selling at the top of the shelf.
  // NOTE: aggregation uses the pack_size value AS RETURNED by the backend —
  // the backend resolves user-defined aliases (e.g. "60 Capsules"→"60
  // Servings") via the attribute_aliases collection BEFORE returning rows.
  // Click the pencil icon on any chip to manage the mapping.
  const availablePackSizes = React.useMemo(() => {
    const ranked = (products || [])
      .filter(r => r.rank != null && (r.pack_size || "").trim())
      .slice()
      .sort((a, b) => (a.rank ?? 1e9) - (b.rank ?? 1e9))
      .slice(0, 10);
    const m = new Map();
    ranked.forEach(r => {
      const p = (r.pack_size || "").trim();
      if (p) m.set(p, (m.get(p) || 0) + 1);
    });
    return Array.from(m.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ label, count }));
  }, [products]);

  // null = include all pack sizes (default); Set means user-restricted subset
  const [selectedPacks, setSelectedPacks] = useState(null);

  // Apply pack-size filter
  const filteredProducts = React.useMemo(() => {
    if (!selectedPacks || selectedPacks.size === 0) return products || [];
    return (products || []).filter(r => r.pack_size && selectedPacks.has(r.pack_size));
  }, [products, selectedPacks]);

  if (!products?.length) {
    return <div className="text-xs text-slate-400 py-12 text-center">No per-brand price data captured.</div>;
  }
  const fmtP = (n) => fmtMoney(n, marketplace);

  // Aggregate per brand from the FILTERED product set
  const map = {};
  filteredProducts.forEach(r => {
    const b = (r.brand || "").trim();
    if (!b || r.price == null) return;
    if (!map[b]) map[b] = { brand: b, sum: 0, n: 0, units: 0, anyUnits: false };
    map[b].sum += r.price;
    map[b].n += 1;
    const u = r.bought_past_month_num;
    if (u != null) { map[b].units += u; map[b].anyUnits = true; }
  });
  const brandRows = Object.values(map)
    .map(x => ({
      brand: x.brand,
      avg: x.sum / x.n,
      n: x.n,
      units: x.anyUnits ? x.units : null,
    }));

  // Category quartiles — computed from filtered set so "like-with-like" pack
  // sizes get their own sensible price bands instead of one global tier.
  const allPrices = filteredProducts.map(r => r.price).filter(p => p != null).sort((a, b) => a - b);
  const q = (p) => {
    if (allPrices.length === 0) return 0;
    const i = (allPrices.length - 1) * p;
    const lo = Math.floor(i), hi = Math.ceil(i);
    if (lo === hi) return allPrices[lo];
    return allPrices[lo] + (allPrices[hi] - allPrices[lo]) * (i - lo);
  };
  const Q1 = q(0.25), Q2 = q(0.50), Q3 = q(0.75);

  // Rank brands by demand within each zone (units sold > listing count)
  const rankSort = (a, b) => {
    if (a.units != null && b.units != null && a.units !== b.units) return b.units - a.units;
    if (a.n !== b.n) return b.n - a.n;
    return b.avg - a.avg;
  };

  // 2×2 layout: [top-left, top-right, bottom-left, bottom-right]
  const ZONES = [
    {
      key: "upper_mid", label: "Upper Mid",
      range: `${fmtP(Q2)} – ${fmtP(Q3)}`,
      match: (avg) => avg >= Q2 && avg < Q3,
      bg: "bg-[#f4f4f0]",          // soft warm grey
      ring: "ring-slate-200",
      labelText: "text-slate-600",
      accent: "text-slate-700",
      priceText: "text-slate-600",
      cornerNum: "2",
    },
    {
      key: "premium", label: "Premium",
      range: `over ${fmtP(Q3)}`,
      match: (avg) => avg >= Q3,
      bg: "bg-[#fef9e7]",          // warm cream / pale yellow
      ring: "ring-amber-200",
      labelText: "text-amber-700",
      accent: "text-amber-800",
      priceText: "text-amber-700",
      cornerNum: "1",
    },
    {
      key: "budget", label: "Budget Friendly",
      range: `under ${fmtP(Q1)}`,
      match: (avg) => avg < Q1,
      bg: "bg-[#f3f7e8]",          // pale olive / mint cream
      ring: "ring-emerald-200",
      labelText: "text-emerald-700",
      accent: "text-emerald-800",
      priceText: "text-emerald-700",
      cornerNum: "3",
    },
    {
      key: "mid_value", label: "Mid Value",
      range: `${fmtP(Q1)} – ${fmtP(Q2)}`,
      match: (avg) => avg >= Q1 && avg < Q2,
      bg: "bg-[#fbeee6]",          // soft peach / rose
      ring: "ring-rose-200",
      labelText: "text-rose-700",
      accent: "text-rose-800",
      priceText: "text-rose-700",
      cornerNum: "4",
    },
  ];

  // Map brand → its row (avg/n/units) for fast lookup
  const brandByName = {};
  brandRows.forEach(b => { brandByName[(b.brand || "").toLowerCase()] = b; });

  const zoneBrands = ZONES.map(z => {
    const allInZone = brandRows.filter(b => z.match(b.avg));
    // Products belonging to this zone (used for drill-down on click)
    const zoneProducts = filteredProducts.filter(p => {
      const br = (p.brand || "").trim().toLowerCase();
      if (!br) return false;
      const row = brandByName[br];
      return row && z.match(row.avg);
    });
    return {
      ...z,
      brands: allInZone.sort(rankSort).slice(0, 5),
      allBrands: allInZone.sort(rankSort),
      totalInZone: allInZone.length,
      products: zoneProducts,
    };
  });

  const togglePack = (label) => {
    setSelectedPacks(prev => {
      const next = new Set(prev || []);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next.size === 0 ? null : next;
    });
  };
  const clearPacks = () => setSelectedPacks(null);
  const activePackCount = selectedPacks ? selectedPacks.size : 0;
  const productsInScope = filteredProducts.length;

  return (
    <div className="relative">
      {/* Pack-size filter — only render when we actually have pack-size data */}
      {availablePackSizes.length > 1 && (
        <div className="mb-5 rounded-2xl bg-gradient-to-br from-indigo-50/60 to-violet-50/40 border border-indigo-100 p-4">
          <div className="flex items-center justify-between gap-3 mb-2.5 flex-wrap">
            <div className="flex items-center gap-2">
              <Package className="w-3.5 h-3.5 text-indigo-500" />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-indigo-700">
                Compare like-with-like · pack size
              </span>
              <span className="text-[10px] text-slate-500 font-mono tabular-nums">
                · top 10 bestsellers · {productsInScope} of {products.length} products
              </span>
            </div>
            {activePackCount > 0 && (
              <button
                type="button"
                onClick={clearPacks}
                className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 hover:underline px-2 py-1 rounded transition-colors"
                data-testid="price-quadrant-clear-packs"
              >
                Clear filter
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {availablePackSizes.map(p => {
              const active = selectedPacks?.has(p.label);
              const allSelected = !selectedPacks;
              return (
                <div
                  key={p.label}
                  className={`group inline-flex items-stretch rounded-full border overflow-hidden transition-all ${
                    active
                      ? "bg-indigo-600 border-indigo-600 shadow-sm"
                      : allSelected
                      ? "bg-white border-indigo-200 hover:border-indigo-400"
                      : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => togglePack(p.label)}
                    className={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 text-[11px] font-semibold transition-colors ${
                      active
                        ? "text-white"
                        : allSelected
                        ? "text-indigo-700"
                        : "text-slate-500 group-hover:text-slate-700"
                    }`}
                    data-testid={`price-quadrant-pack-${p.label}`}
                  >
                    <span>{p.label}</span>
                    <span className={`text-[9px] font-mono tabular-nums ${active ? "text-indigo-100" : "text-slate-400"}`}>
                      ×{p.count}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onEditPackAlias && onEditPackAlias(p.label); }}
                    className={`inline-flex items-center justify-center w-6 border-l opacity-0 group-hover:opacity-100 transition-all ${
                      active
                        ? "border-indigo-500 text-indigo-100 hover:bg-indigo-700"
                        : "border-indigo-100 text-indigo-400 hover:text-indigo-700 hover:bg-indigo-50"
                    }`}
                    title={`Standardize "${p.label}" — map to a clean display label (e.g. "60 Capsules" → "60 Servings")`}
                    data-testid={`price-quadrant-pack-alias-${p.label}`}
                  >
                    <Pencil className="w-2.5 h-2.5" />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="text-[9.5px] text-slate-500 mt-2 italic">
            {activePackCount === 0
              ? "Chips show pack sizes seen in the top-10 bestsellers — click to compare only specific sizes."
              : `Comparing ${activePackCount} pack size${activePackCount === 1 ? '' : 's'} — price bands recomputed for this subset.`}
          </div>
        </div>
      )}

      {/* Empty-state — differentiate "no data at all" from "filter wiped it" */}
      {filteredProducts.length === 0 || brandRows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 py-12 text-center">
          {activePackCount > 0 && filteredProducts.length === 0 ? (
            <>
              <div className="text-xs text-slate-500 font-semibold">No products match this pack-size filter</div>
              <div className="text-[10px] text-slate-400 mt-1">Try unselecting some pack sizes or click "Clear filter".</div>
            </>
          ) : (
            <>
              <div className="text-xs text-slate-500 font-semibold">No per-brand price data for this scope</div>
              <div className="text-[10px] text-slate-400 mt-1">Products in this view don't have both brand and price captured yet.</div>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Axis label — visual cue that y-axis represents price (matches user's
              reference where ₹X sits at the midpoint dividing low/high). */}
          <div className="flex items-center justify-center mb-2">
            <div className="text-[10px] font-bold tracking-wide text-slate-500 font-mono tabular-nums">
              ↑ price ↑   ·   median {fmtP(Q2)}
            </div>
          </div>

          {/* 2×2 matrix — submerged look (no gaps; cells share edges) */}
          <div className="grid grid-cols-2 gap-0 rounded-2xl overflow-hidden ring-1 ring-slate-200">
            {zoneBrands.map(z => {
              const clickable = !!onZoneClick && z.totalInZone > 0;
              return (
                <div
                  key={z.key}
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={clickable ? () => onZoneClick(z) : undefined}
                  onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onZoneClick(z); } } : undefined}
                  className={`group relative ${z.bg} p-5 sm:p-6 min-h-[220px] flex flex-col overflow-hidden transition-all ${
                    clickable ? "cursor-pointer hover:z-10 hover:ring-2 hover:ring-indigo-300 hover:shadow-lg" : ""
                  }`}
                  title={clickable ? `Click to view ${z.totalInZone} brand${z.totalInZone === 1 ? '' : 's'} in this band` : undefined}
                  data-testid={`price-quadrant-${z.key}`}
                >
                  {/* Giant corner number watermark */}
                  <span className={`absolute bottom-3 right-4 text-[80px] font-bold leading-none ${z.accent} opacity-[0.07] select-none pointer-events-none`}>
                    {z.cornerNum}
                  </span>

                {/* Header */}
                <div className="relative">
                  <div className={`text-base sm:text-lg font-bold ${z.labelText}`}>
                    {z.label}
                    <span className="text-[11px] font-mono font-semibold text-slate-500 ml-2 tabular-nums">
                      ({z.range})
                    </span>
                  </div>
                </div>

                {/* Brands */}
                <div className="relative mt-5 flex-1 flex flex-col">
                  {z.brands.length === 0 ? (
                    <div className="text-[11px] text-slate-400 italic flex-1 flex items-center justify-center">
                      no brands in this band
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-x-5 gap-y-3 items-start">
                      {z.brands.map((b) => (
                        <div
                          key={b.brand}
                          className="group inline-flex flex-col items-start gap-0.5 max-w-[160px] transition-transform hover:scale-[1.04]"
                          title={`${b.brand} · ${b.n} product${b.n === 1 ? '' : 's'} · avg ${fmtP(b.avg)}${b.units != null ? ` · ${fmtCount(b.units)} units` : ''}`}
                        >
                          <div className={`px-2.5 py-1.5 rounded-md bg-white/85 ring-1 ${z.ring} text-[12px] sm:text-[13px] font-bold ${z.accent} shadow-sm group-hover:shadow truncate max-w-full`}>
                            {b.brand}
                          </div>
                          <div className={`text-[12px] font-mono tabular-nums ${z.priceText} font-semibold pl-1`}>
                            {fmtP(b.avg)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer chip */}
                <div className="relative mt-4 pt-3 border-t border-current/10 text-[10px] font-mono tabular-nums text-slate-500 flex items-center justify-between">
                  <span className={`font-semibold ${z.labelText}`}>{z.totalInZone} brand{z.totalInZone === 1 ? '' : 's'}</span>
                  <div className="flex items-center gap-2">
                    {z.totalInZone > 5 && <span className="text-slate-400">+{z.totalInZone - 5} more</span>}
                    {clickable && (
                      <span className={`opacity-0 group-hover:opacity-100 inline-flex items-center gap-0.5 ${z.accent} font-semibold transition-opacity`}>
                        view all <ArrowUpRight className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Footnote — quartile thresholds */}
          <div className="mt-3 text-[10px] font-mono text-slate-400 text-center">
            Bands derived from category price quartiles · Q1 {fmtP(Q1)} · Q2 {fmtP(Q2)} · Q3 {fmtP(Q3)}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Rating distribution ────────────────────────────────────────────
const RatingDistBar = ({ distribution }) => {
  const order = ["5★", "4-5★", "3-4★", "2-3★", "<2★", "unrated"];
  const colors = { "5★": "#10b981", "4-5★": "#22c55e", "3-4★": "#f59e0b", "2-3★": "#ef4444", "<2★": "#dc2626", "unrated": "#cbd5e1" };
  const total = order.reduce((s, k) => s + (distribution[k] || 0), 0) || 1;
  return (
    <div className="space-y-3">
      {order.map(k => {
        const v = distribution[k] || 0;
        const pct = (v / total) * 100;
        return (
          <div key={k} className="flex items-center gap-4 text-xs">
            <span className="w-16 text-slate-600 font-semibold">{k}</span>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full transition-all duration-1000 ease-out" style={{ width: `${pct}%`, backgroundColor: colors[k] }} />
            </div>
            <span className="w-12 text-right font-mono text-slate-500 tabular-nums">{v}</span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Brand × Flavour Heatmap (Unique-flavours view) ─────────────────
// Cell filled iff the brand offers this flavour. Row intensity scales with the
// brand's visible-tick count in the top-10 flavour slice — darkest row = widest
// variety in the category. Pill next to each brand shows the same count so it
// always matches the ticks user counts. Cell click still drills into the
// Product Explorer filtered by (brand, flavour).
const Heatmap = ({ heatmapUnique, onCellClick }) => {
  const brands = heatmapUnique?.brands || [];
  const flavours = heatmapUnique?.flavours || [];
  const cells = heatmapUnique?.cells || [];

  // Backend returns every eligible brand (ordered by variety breadth
  // desc, then brand name asc). Default-collapse to 10 so the card
  // stays scannable; "View all" expands to show the long tail of niche
  // brands sitting below the top 10.
  const DEFAULT_LIMIT = 10;
  const [expanded, setExpanded] = useState(false);
  const visibleBrands = expanded ? brands : brands.slice(0, DEFAULT_LIMIT);
  const hiddenCount = Math.max(0, brands.length - DEFAULT_LIMIT);

  const explainer = "Rows sorted by each brand's best bestseller rank (its top-ranked SKU). Amber #N badge = that top rank. Blue number = how many of the top flavours this brand offers. Row darkness scales with the ✓ count — widest-variety brand's row is darkest. Click any ✓ to drill into the products.";

  if (!brands.length || !flavours.length) {
    return (
      <div>
        <HeaderStrip explainer={explainer} />
        <div className="text-xs text-slate-400 py-12 text-center">Not enough variety — need at least 2 brands × 2 flavours.</div>
      </div>
    );
  }

  const cellMap = {};
  cells.forEach(c => { cellMap[`${c.brand}|${c.flavour}`] = c; });

  const brandVisibleCounts = heatmapUnique?.brand_unique_counts || {};
  const brandBestRanks = heatmapUnique?.brand_best_ranks || {};
  // Scale is anchored to the FULL brand set so toggling "View all" doesn't
  // suddenly re-shade the top-10 rows (would be visually jarring).
  const maxVisible = Math.max(1, ...brands.map(b => brandVisibleCounts[b] || 0));
  const minVisible = Math.min(...brands.map(b => brandVisibleCounts[b] || 0), maxVisible);

  const rowRgba = (v, vmax) => `rgba(99, 102, 241, ${0.20 + (v / (vmax || 1)) * 0.78})`;

  return (
    <div>
      <HeaderStrip explainer={explainer} />
      <div className="flex items-start gap-4 overflow-x-auto">
        <table className="text-[10px] border-separate" style={{ borderSpacing: 3 }}>
          <thead>
            <tr>
              <th></th>
              {flavours.map(f => (
                <th key={f} className="text-slate-500 font-semibold px-1 pb-2 text-center align-bottom">
                  <div className="truncate max-w-[80px] mx-auto" title={f}>{f}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleBrands.map(b => {
              const rowVisible = brandVisibleCounts[b] || 0;
              const rowIntensity = rowVisible / maxVisible;
              const bestRank = brandBestRanks[b];
              return (
                <tr key={b}>
                  <th
                    className="text-left text-slate-700 font-semibold px-3 py-0.5 whitespace-nowrap"
                    title={
                      bestRank
                        ? `${b} · best bestseller rank #${bestRank} · covers ${rowVisible} of the top-${flavours.length} flavours shown`
                        : `${b} covers ${rowVisible} of the top-${flavours.length} flavours shown`
                    }
                  >
                    {b}
                    {bestRank && (
                      <span className="ml-1.5 inline-flex items-center justify-center min-w-[22px] h-[16px] px-1 rounded-full bg-amber-50 text-amber-700 text-[9.5px] font-bold tabular-nums align-middle" title={`Top SKU rank: #${bestRank}`}>
                        #{bestRank}
                      </span>
                    )}
                    <span className="ml-1 inline-flex items-center justify-center min-w-[22px] h-[16px] px-1 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-bold tabular-nums align-middle" title={`${rowVisible} ✓ in this row`}>
                      {rowVisible}
                    </span>
                  </th>
                  {flavours.map(f => {
                    const cell = cellMap[`${b}|${f}`] || { has: false, count: 0 };
                    const skuCount = cell.count || 0;
                    const isFilled = !!cell.has;
                    const alpha = isFilled ? (0.20 + rowIntensity * 0.78) : 0;
                    const bg = isFilled ? `rgba(99, 102, 241, ${alpha})` : "#f8fafc";
                    const fg = rowIntensity > 0.55 ? "#fff" : "#475569";
                    const titleText = isFilled
                      ? `${b} sells ${f} (${skuCount} SKU${skuCount === 1 ? '' : 's'}) — click to see products`
                      : `${b} doesn't offer ${f}`;
                    const clickable = isFilled && !!onCellClick;
                    return (
                      <td
                        key={f}
                        onClick={clickable ? () => onCellClick(b, f) : undefined}
                        className={`text-center w-11 h-8 rounded-md transition-all font-mono tabular-nums ${clickable ? 'hover:scale-110 hover:ring-2 hover:ring-indigo-400 cursor-pointer' : 'cursor-default'}`}
                        style={{ backgroundColor: bg, color: fg, fontWeight: isFilled ? 700 : 400 }}
                        title={titleText}
                        data-testid={clickable ? `dd-heatmap-cell-${b}-${f}` : undefined}
                      >
                        {isFilled ? "✓" : ""}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Right-side gradient legend — anchors the "darker = wider variety" intuition. */}
        <div className="flex flex-col items-center gap-1 pt-6 shrink-0" data-testid="heatmap-legend">
          <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">Variety</div>
          <div className="text-[10px] font-mono tabular-nums text-slate-600 font-bold">{maxVisible}</div>
          <div
            className="w-3 rounded-sm"
            style={{
              height: 140,
              background: `linear-gradient(to top, ${rowRgba(0, maxVisible)}, ${rowRgba(maxVisible, maxVisible)})`,
            }}
          />
          <div className="text-[10px] font-mono tabular-nums text-slate-500">{Math.max(1, minVisible)}</div>
        </div>
      </div>

      {/* View-all toggle — collapsed by default so the card stays dense */}
      {hiddenCount > 0 && (
        <div className="flex items-center justify-center mt-3">
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full text-[10.5px] font-semibold border border-indigo-200 bg-white hover:bg-indigo-50 text-indigo-700 transition-colors"
            title={expanded ? "Collapse to the top 10 brands by variety" : `Show every brand that offers at least one of the top ${flavours.length} flavours`}
            data-testid="dd-heatmap-view-all"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-3 h-3" />
                Show top {DEFAULT_LIMIT}
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3" />
                View all {brands.length} brands
                <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full bg-indigo-100 text-indigo-700 text-[9px] font-bold tabular-nums">
                  +{hiddenCount}
                </span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// Header (info icon only — mode toggle removed per product decision to keep
// the Unique-flavours view as the single source of truth for this heatmap).
const HeaderStrip = ({ explainer }) => (
  <div className="flex items-center gap-2 mb-3">
    <button
      type="button"
      className="inline-flex items-center justify-center w-4 h-4 rounded-full text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors cursor-help"
      title={explainer}
      aria-label={explainer}
      data-testid="heatmap-mode-explainer"
    >
      <Info className="w-3.5 h-3.5" />
    </button>
  </div>
);

// ─── Scatter ────────────────────────────────────────────────────────
const ScatterPlot = ({ scatter }) => {
  if (!scatter?.length) return <div className="text-xs text-slate-400 py-12 text-center">Need price + rating to plot.</div>;
  return (
    <ResponsiveContainer width="100%" height={320}>
      <ScatterChart margin={{ top: 12, right: 20, bottom: 28, left: 4 }}>
        <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" />
        <XAxis type="number" dataKey="price" name="Price" tickFormatter={fmtINR}
          tick={{ fontSize: 11, fill: "#64748b", fontFamily: "ui-monospace, monospace" }}
          label={{ value: "Price", position: "bottom", offset: -8, fontSize: 11, fill: "#94a3b8" }} />
        <YAxis type="number" dataKey="rating" name="Rating" domain={[0, 5]}
          tick={{ fontSize: 11, fill: "#64748b", fontFamily: "ui-monospace, monospace" }}
          label={{ value: "Rating", angle: -90, position: "insideLeft", fontSize: 11, fill: "#94a3b8" }} />
        <ZAxis type="number" dataKey="reviews_count" range={[40, 1000]} name="Reviews" />
        <Tooltip
          cursor={{ strokeDasharray: "3 3" }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0].payload;
            return (
              <div className="bg-white border border-slate-200 rounded-lg p-2.5 shadow-lg text-[11px]">
                <div className="font-bold text-slate-800 mb-0.5">{d.brand || "—"}</div>
                <div className="text-slate-600 max-w-[240px] truncate">{d.title}</div>
                <div className="mt-1.5 grid grid-cols-3 gap-3 text-slate-500 font-mono tabular-nums">
                  <span>₹{d.price}</span>
                  <span className="text-amber-500">★{d.rating}</span>
                  <span className="text-emerald-600">{fmtCount(d.reviews_count)}</span>
                </div>
              </div>
            );
          }}
        />
        <Scatter data={scatter} fill="#6366f1" fillOpacity={0.55} />
      </ScatterChart>
    </ResponsiveContainer>
  );
};

// ─── Product Explorer ───────────────────────────────────────────────
const ProductExplorer = ({ products, category, onEditBrandAlias, onEditAlias, onAliasesUpdated, onStageManual }) => {
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("rank");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(0);
  const [uploading, setUploading] = useState(false);
  // Inline "+ Add" state — {asin, attribute} identifies which blank cell
  // is currently in edit mode; `draft` holds the typed value; `saving`
  // guards against double-submits.
  const [editingBlank, setEditingBlank] = useState(null);
  const [draftValue, setDraftValue] = useState("");
  const [savingBlank, setSavingBlank] = useState(false);
  const fileInputRef = useRef(null);
  const PAGE_SIZE = 15;

  // ─── Column visibility ────────────────────────────────────────────
  // Users can hide any column except the "Product" identity column
  // (ASIN + title + thumbnail). Selections persist per category in
  // localStorage so repeat visits remember the preferred layout.
  const EXPLORER_COLUMNS = useMemo(() => ([
    { id: "rank",                 label: "Rank",         always: false },
    { id: "product",              label: "Product",      always: true  },
    { id: "brand",                label: "Brand",        always: false },
    { id: "flavour",              label: "Flavour",      always: false },
    { id: "item_form",            label: "Form",         always: false },
    { id: "diet_type",            label: "Diet",         always: false },
    { id: "specialty",            label: "Specialty",    always: false },
    { id: "pack_size",            label: "Pack",         always: false },
    { id: "price",                label: "Price",        always: false },
    { id: "rating",               label: "Rating",       always: false },
    { id: "reviews_count",        label: "Reviews",      always: false },
    { id: "bought_past_month",    label: "Sold/mo",      always: false },
    { id: "marketplace",          label: "Marketplace",  always: false },
    { id: "link",                 label: "External link", always: false },
  ]), []);
  const colStorageKey = `dd-explorer-cols:${(category || "_all").toLowerCase()}`;
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem(colStorageKey);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return new Set(arr);
      }
    } catch { /* ignore corrupted entries */ }
    return new Set(EXPLORER_COLUMNS.map(c => c.id));
  });
  // Reload persisted set whenever the category changes.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(colStorageKey);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) { setVisibleCols(new Set(arr)); return; }
      }
    } catch { /* ignore */ }
    setVisibleCols(new Set(EXPLORER_COLUMNS.map(c => c.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colStorageKey]);
  useEffect(() => {
    try { localStorage.setItem(colStorageKey, JSON.stringify(Array.from(visibleCols))); } catch { /* quota */ }
  }, [visibleCols, colStorageKey]);
  const isCol = (id) => {
    const def = EXPLORER_COLUMNS.find(c => c.id === id);
    if (def && def.always) return true;
    return visibleCols.has(id);
  };
  const toggleCol = (id) => {
    const def = EXPLORER_COLUMNS.find(c => c.id === id);
    if (def && def.always) return; // locked
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const [colsMenuOpen, setColsMenuOpen] = useState(false);
  const colsMenuRef = useRef(null);
  useEffect(() => {
    if (!colsMenuOpen) return;
    const onDocClick = (e) => {
      if (colsMenuRef.current && !colsMenuRef.current.contains(e.target)) setColsMenuOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setColsMenuOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [colsMenuOpen]);
  const hiddenCount = EXPLORER_COLUMNS.filter(c => !c.always && !visibleCols.has(c.id)).length;
  const visibleColCount = EXPLORER_COLUMNS.filter(c => isCol(c.id)).length;

  const startBlankEdit = (asin, attribute) => {
    setEditingBlank({ asin, attribute });
    setDraftValue("");
  };
  const cancelBlankEdit = () => {
    setEditingBlank(null);
    setDraftValue("");
  };
  const saveBlankEdit = async () => {
    if (!editingBlank || !draftValue.trim() || savingBlank) return;
    // Batch-save mode: hand the change off to the parent's pending queue
    // and close the inline input. The parent applies the value
    // optimistically and shows a "Save changes" pill that flushes every
    // pending edit in one round-trip.
    if (onStageManual) {
      onStageManual({
        asin: editingBlank.asin,
        attribute: editingBlank.attribute,
        value: draftValue.trim(),
      });
      setEditingBlank(null);
      setDraftValue("");
      return;
    }
    setSavingBlank(true);
    try {
      const res = await axios.post(`${API}/amazon/bestseller/analytics/manual-attribute`, {
        asin: editingBlank.asin,
        attribute: editingBlank.attribute,
        value: draftValue.trim(),
      });
      const d = res.data || {};
      toast.success(`Saved · ${d.rows_updated} row${d.rows_updated === 1 ? '' : 's'} across ${d.jobs_updated} job${d.jobs_updated === 1 ? '' : 's'}.`);
      setEditingBlank(null);
      setDraftValue("");
      if (onAliasesUpdated) {
        try { await onAliasesUpdated(); } catch { /* toast already shown */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSavingBlank(false);
  };

  // Shared renderer for the 6 attribute cells. Renders one of:
  //   1) value + hover pencil (existing standardize flow)
  //   2) "— + Add" placeholder when the scrape returned nothing
  //   3) inline input with Save / Cancel while the user types a manual value
  // Kept as a plain function (not a nested component) to avoid the
  // react/no-unstable-nested-components rule and preserve focus behaviour.
  const renderAttrCell = (p, attribute, testidKey) => {
    const value = p[attribute];
    const isEditing = editingBlank && editingBlank.asin === p.asin && editingBlank.attribute === attribute;
    if (isEditing) {
      return (
        <div className="inline-flex items-center gap-1">
          <input
            type="text"
            value={draftValue}
            autoFocus
            onChange={(e) => setDraftValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); saveBlankEdit(); }
              else if (e.key === "Escape") { e.preventDefault(); cancelBlankEdit(); }
            }}
            disabled={savingBlank}
            placeholder="Type value…"
            className="h-6 px-1.5 text-[11px] rounded border border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-400 w-24 tabular-nums"
            data-testid={`dd-explorer-${testidKey}-input-${p.asin}`}
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); saveBlankEdit(); }}
            disabled={savingBlank || !draftValue.trim()}
            className="w-5 h-5 rounded inline-flex items-center justify-center text-emerald-600 hover:bg-emerald-50 disabled:opacity-40 transition-all"
            title="Save (Enter)"
            data-testid={`dd-explorer-${testidKey}-save-${p.asin}`}
          >
            {savingBlank ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); cancelBlankEdit(); }}
            disabled={savingBlank}
            className="w-5 h-5 rounded inline-flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all"
            title="Cancel (Esc)"
            data-testid={`dd-explorer-${testidKey}-cancel-${p.asin}`}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }
    if (value) {
      const isBrand = attribute === "brand";
      // Alias modal must receive the RAW (unaliased) value so the mapping
      // created is keyed by the actual scrape/bucket value. If we passed
      // the display value, a re-edit would create a NEW alias
      // (display → new_display) that never matches the underlying data.
      const rawForAlias = p[`${attribute}_raw`] || value;
      const onClickEdit = isBrand
        ? () => onEditBrandAlias && onEditBrandAlias(rawForAlias)
        : () => onEditAlias && onEditAlias(attribute, rawForAlias);
      const canEdit = isBrand ? !!onEditBrandAlias : !!onEditAlias;
      return (
        <div className="inline-flex items-center gap-1 group/cell">
          <span>{value}</span>
          {canEdit && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClickEdit(); }}
              className="opacity-0 group-hover/cell:opacity-100 w-5 h-5 rounded inline-flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
              title={`Standardize "${value}"`}
              data-testid={`dd-explorer-${testidKey}-edit-${p.asin}`}
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </div>
      );
    }
    // Blank cell — surface a subtle "+ Add" affordance on hover.
    return (
      <div className="inline-flex items-center gap-1 group/cell">
        <span className="text-slate-300">—</span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); startBlankEdit(p.asin, attribute); }}
          className="opacity-0 group-hover/cell:opacity-100 inline-flex items-center gap-0.5 h-5 px-1.5 rounded text-[10px] font-semibold text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 border border-transparent hover:border-emerald-200 transition-all"
          title={`Manually set ${attribute.replace('_', ' ')} for ${p.asin}`}
          data-testid={`dd-explorer-${testidKey}-add-${p.asin}`}
        >
          <Plus className="w-2.5 h-2.5" />
          Add
        </button>
      </div>
    );
  };

  const filtered = useMemo(() => {
    const qNorm = q.trim().toLowerCase();
    let out = products;
    if (qNorm) {
      out = out.filter(p =>
        (p.title || "").toLowerCase().includes(qNorm) ||
        (p.brand || "").toLowerCase().includes(qNorm) ||
        (p.asin || "").toLowerCase().includes(qNorm) ||
        (p.flavour || "").toLowerCase().includes(qNorm) ||
        (p.item_form || "").toLowerCase().includes(qNorm) ||
        (p.diet_type || "").toLowerCase().includes(qNorm) ||
        (p.specialty || "").toLowerCase().includes(qNorm) ||
        (p.pack_size || "").toLowerCase().includes(qNorm)
      );
    }
    out = [...out].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const aNull = av === null || av === undefined;
      const bNull = bv === null || bv === undefined;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      return sortDir === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return out;
  }, [products, q, sortKey, sortDir]);

  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };
  const SortIcon = ({ k }) => sortKey !== k ? null : (sortDir === "asc" ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />);

  // CSV export — exports the CURRENTLY-FILTERED set (respects search box).
  // Fields mirror the on-screen columns 1:1 so what you see is what you get.
  const exportCsv = () => {
    const header = [
      "Rank", "ASIN", "Title", "Brand", "Flavour", "Item Form", "Diet Type",
      "Specialty", "Pack Size", "Price (INR)", "Rating", "Reviews",
      "Sold Past Month", "Marketplace", "URL",
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      // Quote if the value contains comma / quote / newline.
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [header.join(",")];
    for (const p of filtered) {
      lines.push([
        p.rank ?? "",
        p.asin || "",
        p.title || "",
        p.brand || "",
        p.flavour || "",
        p.item_form || "",
        p.diet_type || "",
        p.specialty || "",
        p.pack_size || "",
        p.price ?? "",
        p.rating ?? "",
        p.reviews_count ?? "",
        p.bought_past_month_str || (p.bought_past_month_num ?? ""),
        p.marketplace || "",
        p.url || "",
      ].map(esc).join(","));
    }
    const blob = new Blob([lines.join("\n") + "\n"], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `horizon-explorer-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  // Mapping-template download — one row per unique (attribute, raw_value)
  // present in the CURRENTLY-FILTERED explorer view. `display_value` is
  // left blank so the analyst fills it offline. Import via the "Upload
  // mappings" button below and every ASIN in this category will use the
  // standardized value on the next deep-dive render.
  const ATTR_KEYS = ["brand", "flavour", "item_form", "diet_type", "specialty", "pack_size"];
  const csvEscape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const downloadMappingTemplate = () => {
    // Dedupe (attribute, raw_value) pairs across the filtered set. Attribute
    // key uses the backend's canonical snake_case names (brand, flavour,
    // item_form, diet_type, specialty, pack_size) so the bulk endpoint's
    // `_canonical_attr_key` accepts them 1:1.
    const seen = new Set();
    const rows = [];
    for (const p of filtered) {
      for (const attr of ATTR_KEYS) {
        const raw = (p[attr] || "").trim();
        if (!raw || raw === "—") continue;
        const key = `${attr}|${raw.toLowerCase()}`;
        if (seen.has(key)) continue;
        seen.add(key);
        rows.push({ attribute: attr, raw_value: raw });
      }
    }
    rows.sort((a, b) => a.attribute.localeCompare(b.attribute) || a.raw_value.localeCompare(b.raw_value));
    const header = ["attribute", "raw_value", "display_value", "category"];
    const lines = [header.join(",")];
    for (const r of rows) {
      lines.push([r.attribute, r.raw_value, "", category || ""].map(csvEscape).join(","));
    }
    if (!rows.length) {
      // Still emit the header + a hint so the user has a canonical example.
      lines.push(["brand", "e.g. MuscleBlaze", "MuscleBlaze", category || ""].map(csvEscape).join(","));
    }
    const blob = new Blob([lines.join("\n") + "\n"], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `horizon-mappings-template-${(category || "all").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success(`Template ready · ${rows.length} unique attribute value${rows.length === 1 ? "" : "s"}. Fill in "display_value" and re-upload.`);
  };

  // CSV → attribute-alias bulk upsert. Parses `attribute,raw_value,display_value,category?`
  // rows, tolerates quoted fields (RFC-ish), skips blank/incomplete rows,
  // and posts to /attribute-aliases/bulk in one shot. On success, calls
  // the parent refetch so the new mappings surface in Deep Dive immediately.
  const parseCsvRow = (line) => {
    const out = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') { inQuotes = false; }
        else { cur += ch; }
      } else {
        if (ch === '"') inQuotes = true;
        else if (ch === ",") { out.push(cur); cur = ""; }
        else cur += ch;
      }
    }
    out.push(cur);
    return out.map(s => s.trim());
  };

  const onFilePicked = async (evt) => {
    const file = evt.target.files && evt.target.files[0];
    evt.target.value = ""; // reset so the same file can be re-uploaded later
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      // Split on both \r\n and \n; drop trailing blank lines.
      const rawLines = text.split(/\r?\n/).filter(l => l.length > 0);
      if (!rawLines.length) {
        toast.error("CSV is empty.");
        setUploading(false);
        return;
      }
      // Header sniff — accept both `attribute,raw_value,display_value[,category]`
      // and header-less files if col count is 3+.
      let start = 0;
      const first = parseCsvRow(rawLines[0]).map(s => s.toLowerCase());
      const hasHeader = first.includes("attribute") && first.includes("raw_value") && first.includes("display_value");
      if (hasHeader) start = 1;

      const aliases = [];
      const errors = [];
      for (let i = start; i < rawLines.length; i++) {
        const cols = parseCsvRow(rawLines[i]);
        if (cols.length < 3) { errors.push(`Row ${i + 1}: expected ≥3 columns, got ${cols.length}`); continue; }
        const [attribute, raw_value, display_value, cat] = cols;
        if (!attribute || !raw_value || !display_value) { errors.push(`Row ${i + 1}: attribute/raw_value/display_value required`); continue; }
        aliases.push({
          attribute: attribute.trim(),
          raw_value: raw_value.trim(),
          display_value: display_value.trim(),
          category: (cat || category || "").trim() || null,
        });
      }
      if (!aliases.length) {
        toast.error(`No valid rows found${errors.length ? ` · ${errors.length} error(s)` : ""}.`);
        setUploading(false);
        return;
      }
      const res = await axios.post(`${API}/attribute-aliases/bulk`, { aliases });
      const d = res.data || {};
      const errCount = (d.errors || []).length + errors.length;
      toast.success(`Mappings applied: ${d.created || 0} created · ${d.updated || 0} updated${errCount ? ` · ${errCount} skipped` : ""}.`);
      if (onAliasesUpdated) {
        try { await onAliasesUpdated(); } catch { /* toast already shown */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
    }
    setUploading(false);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            placeholder="Search title, ASIN, brand, flavour, form, diet, specialty, pack…"
            className="w-full h-10 pl-9 pr-3 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-indigo-400 transition-colors"
            data-testid="dd-explorer-search"
          />
        </div>
        <span className="text-[10px] font-mono text-slate-500 tabular-nums">{filtered.length}/{products.length}</span>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={onFilePicked}
          data-testid="dd-explorer-upload-file-input"
        />
        <button
          type="button"
          onClick={downloadMappingTemplate}
          className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg text-[11px] font-semibold border border-teal-200 bg-white hover:bg-teal-50 text-teal-700 transition-colors"
          title="Download a pre-filled mapping template for every raw attribute value in this view"
          data-testid="dd-explorer-mapping-template"
        >
          <FileDown className="w-3.5 h-3.5" />
          Sample template
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg text-[11px] font-semibold border border-emerald-200 bg-white hover:bg-emerald-50 text-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          title="Upload a mappings CSV (attribute, raw_value, display_value[, category])"
          data-testid="dd-explorer-upload-mappings"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? "Uploading…" : "Upload mappings"}
        </button>
        <button
          type="button"
          onClick={exportCsv}
          disabled={!filtered.length}
          className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg text-[11px] font-semibold border border-indigo-200 bg-white hover:bg-indigo-50 text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title={`Export ${filtered.length} row${filtered.length === 1 ? "" : "s"} to CSV`}
          data-testid="dd-explorer-export-csv"
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
        {/* ─── Columns dropdown ───────────────────────────────────── */}
        <div className="relative" ref={colsMenuRef}>
          <button
            type="button"
            onClick={() => setColsMenuOpen(v => !v)}
            className={`inline-flex items-center gap-1.5 px-3 h-10 rounded-lg text-[11px] font-semibold border transition-colors ${
              hiddenCount > 0
                ? "border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800"
                : "border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
            }`}
            title={hiddenCount > 0 ? `${hiddenCount} column${hiddenCount === 1 ? "" : "s"} hidden` : "Show or hide columns"}
            data-testid="dd-explorer-columns-toggle"
            aria-expanded={colsMenuOpen}
          >
            <Columns3 className="w-3.5 h-3.5" />
            Columns
            {hiddenCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-bold bg-amber-200 text-amber-900 tabular-nums">
                {hiddenCount}
              </span>
            )}
            <ChevronDown className={`w-3 h-3 transition-transform ${colsMenuOpen ? "rotate-180" : ""}`} />
          </button>
          {colsMenuOpen && (
            <div
              className="absolute right-0 top-full mt-1.5 w-64 rounded-xl border border-slate-200 bg-white shadow-xl z-30 overflow-hidden"
              data-testid="dd-explorer-columns-menu"
            >
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50/60">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                  Columns · {visibleColCount}/{EXPLORER_COLUMNS.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setVisibleCols(new Set(EXPLORER_COLUMNS.map(c => c.id)))}
                    className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 px-1.5 py-0.5 rounded hover:bg-indigo-50 transition-colors"
                    data-testid="dd-explorer-columns-show-all"
                  >
                    Show all
                  </button>
                  <span className="text-slate-300 text-[10px]">·</span>
                  <button
                    type="button"
                    onClick={() => setVisibleCols(new Set(EXPLORER_COLUMNS.filter(c => c.always).map(c => c.id)))}
                    className="text-[10px] font-semibold text-slate-500 hover:text-slate-800 px-1.5 py-0.5 rounded hover:bg-slate-100 transition-colors"
                    data-testid="dd-explorer-columns-hide-all"
                  >
                    Hide all
                  </button>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto py-1">
                {EXPLORER_COLUMNS.map(col => {
                  const shown = isCol(col.id);
                  return (
                    <label
                      key={col.id}
                      className={`flex items-center gap-2 px-3 py-1.5 text-[11px] cursor-pointer transition-colors ${
                        col.always ? "opacity-60 cursor-not-allowed" : "hover:bg-slate-50"
                      }`}
                      data-testid={`dd-explorer-column-row-${col.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={shown}
                        disabled={col.always}
                        onChange={() => toggleCol(col.id)}
                        className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                        data-testid={`dd-explorer-column-checkbox-${col.id}`}
                      />
                      <span className="flex-1 text-slate-700 font-medium">{col.label}</span>
                      {col.always ? (
                        <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-slate-400">locked</span>
                      ) : shown ? (
                        <Eye className="w-3 h-3 text-emerald-500" />
                      ) : (
                        <EyeOff className="w-3 h-3 text-slate-300" />
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="overflow-x-auto -mx-1 px-1 rounded-xl border border-slate-100 bg-white">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[9px] uppercase tracking-[0.18em] text-slate-400 border-b border-slate-100 bg-slate-50/40">
              {isCol("rank") && <th className="px-3 py-3 cursor-pointer hover:text-indigo-600 font-bold" onClick={() => toggleSort("rank")}>Rank <SortIcon k="rank" /></th>}
              {isCol("product") && <th className="px-3 py-3 font-bold">Product</th>}
              {isCol("brand") && <th className="px-3 py-3 cursor-pointer hover:text-indigo-600 font-bold" onClick={() => toggleSort("brand")}>Brand <SortIcon k="brand" /></th>}
              {isCol("flavour") && <th className="px-3 py-3 cursor-pointer hover:text-indigo-600 font-bold" onClick={() => toggleSort("flavour")}>Flavour <SortIcon k="flavour" /></th>}
              {isCol("item_form") && <th className="px-3 py-3 cursor-pointer hover:text-indigo-600 font-bold" onClick={() => toggleSort("item_form")}>Form <SortIcon k="item_form" /></th>}
              {isCol("diet_type") && <th className="px-3 py-3 cursor-pointer hover:text-indigo-600 font-bold" onClick={() => toggleSort("diet_type")}>Diet <SortIcon k="diet_type" /></th>}
              {isCol("specialty") && <th className="px-3 py-3 cursor-pointer hover:text-indigo-600 font-bold" onClick={() => toggleSort("specialty")}>Specialty <SortIcon k="specialty" /></th>}
              {isCol("pack_size") && <th className="px-3 py-3 cursor-pointer hover:text-indigo-600 font-bold" onClick={() => toggleSort("pack_size")}>Pack <SortIcon k="pack_size" /></th>}
              {isCol("price") && <th className="px-3 py-3 cursor-pointer hover:text-indigo-600 text-right font-bold" onClick={() => toggleSort("price")}>Price <SortIcon k="price" /></th>}
              {isCol("rating") && <th className="px-3 py-3 cursor-pointer hover:text-indigo-600 text-right font-bold" onClick={() => toggleSort("rating")}>★ <SortIcon k="rating" /></th>}
              {isCol("reviews_count") && <th className="px-3 py-3 cursor-pointer hover:text-indigo-600 text-right font-bold" onClick={() => toggleSort("reviews_count")}>Reviews <SortIcon k="reviews_count" /></th>}
              {isCol("bought_past_month") && <th className="px-3 py-3 cursor-pointer hover:text-indigo-600 text-right font-bold" onClick={() => toggleSort("bought_past_month_num")}>Sold/mo <SortIcon k="bought_past_month_num" /></th>}
              {isCol("marketplace") && <th className="px-3 py-3 font-bold">MP</th>}
              {isCol("link") && <th className="px-3 py-3"></th>}
            </tr>
          </thead>
          <tbody>
            {pageRows.map(p => (
              <tr key={p.asin} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors" data-testid={`dd-explorer-row-${p.asin}`}>
                {isCol("rank") && <td className="px-3 py-2.5 font-mono text-slate-400 tabular-nums">{p.rank ?? "—"}</td>}
                {isCol("product") && (
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2 max-w-md">
                      {p.image && <img src={p.image} alt="" className="w-9 h-9 rounded object-cover bg-slate-100 shrink-0" />}
                      <div className="min-w-0">
                        <a href={p.url} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-indigo-600 hover:underline">{p.asin}</a>
                        <div className="text-slate-700 truncate" title={p.title}>{p.title}</div>
                      </div>
                    </div>
                  </td>
                )}
                {isCol("brand") && (
                  <td className="px-3 py-2.5 text-slate-700 font-medium whitespace-nowrap">
                    {renderAttrCell(p, "brand", "brand")}
                  </td>
                )}
                {isCol("flavour") && (
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">
                    {renderAttrCell(p, "flavour", "flavour")}
                  </td>
                )}
                {isCol("item_form") && (
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">
                    {renderAttrCell(p, "item_form", "form")}
                  </td>
                )}
                {isCol("diet_type") && (
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">
                    {renderAttrCell(p, "diet_type", "diet")}
                  </td>
                )}
                {isCol("specialty") && (
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">
                    {renderAttrCell(p, "specialty", "specialty")}
                  </td>
                )}
                {isCol("pack_size") && (
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">
                    {renderAttrCell(p, "pack_size", "pack")}
                  </td>
                )}
                {isCol("price") && <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700">{p.price ? `₹${p.price}` : "—"}</td>}
                {isCol("rating") && <td className="px-3 py-2.5 text-right font-mono tabular-nums text-amber-600">{p.rating ? p.rating.toFixed(1) : "—"}</td>}
                {isCol("reviews_count") && <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700">{p.reviews_count ? fmtCount(p.reviews_count) : "—"}</td>}
                {isCol("bought_past_month") && <td className="px-3 py-2.5 text-right font-mono tabular-nums text-emerald-700">{p.bought_past_month_str || (p.bought_past_month_num ? fmtCount(p.bought_past_month_num) : "—")}</td>}
                {isCol("marketplace") && <td className="px-3 py-2.5 text-slate-500 font-mono text-[10px] uppercase">{p.marketplace || "—"}</td>}
                {isCol("link") && (
                  <td className="px-3 py-2.5 text-right">
                    <a href={p.url} target="_blank" rel="noreferrer" className="text-slate-300 hover:text-indigo-600 inline-flex items-center transition-colors">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </td>
                )}
              </tr>
            ))}
            {pageRows.length === 0 && (
              <tr><td colSpan={visibleColCount} className="text-center py-10 text-xs text-slate-400">No products match your search.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-4 text-[10px]">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="h-8 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors font-semibold">Prev</button>
          <span className="font-mono text-slate-500 tabular-nums px-3">{page + 1} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="h-8 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors font-semibold">Next</button>
        </div>
      )}
    </div>
  );
};

// ─── RankRace — bump chart tracking top-N bestseller positions over time
// Renders an SVG with one smooth line per ASIN. Y-axis is rank (1 at top,
// `top_n` at bottom), X-axis is time. Hover scrubs a vertical guide and
// reveals each track's rank that day. Animated entrance, leader-board
// brand badges anchored to the right edge.
const RANK_PALETTE = [
  "#6366F1", "#EC4899", "#10B981", "#F59E0B", "#06B6D4",
  "#8B5CF6", "#F43F5E", "#84CC16", "#0EA5E9", "#A855F7",
  "#EF4444", "#14B8A6", "#FB923C", "#3B82F6", "#D946EF",
];

const RankRace = ({ category, days = 30, marketplace }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoverX, setHoverX] = useState(null);      // 0-1 along chart
  const [selectedAsin, setSelectedAsin] = useState(null);
  const [animDone, setAnimDone] = useState(false);
  const [detailAsin, setDetailAsin] = useState(null);   // ASIN to show in product detail modal
  const [brandDrill, setBrandDrill] = useState(null);   // brand name → opens list-of-ASINs-for-brand modal
  const [viewMode, setViewMode] = useState("slope");    // "bump" | "slope" — user-togglable visualization (slope is default)
  const svgRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null); setAnimDone(false);
    const params = { category, days, top_n: 10 };
    if (marketplace) params.marketplace = marketplace;
    axios.get(`${API}/amazon/bestseller/rank-race`, { params })
      .then(r => { if (alive) setData(r.data); })
      .catch(e => { if (alive) setError(e?.response?.data?.detail || "Failed to load rank race"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [category, days, marketplace]);

  // Trigger animation after mount
  useEffect(() => {
    if (!data || data.tracks.length === 0) return;
    const t = setTimeout(() => setAnimDone(true), 50);
    return () => clearTimeout(t);
  }, [data]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 p-12 text-center">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2 text-indigo-500" />
        <span className="text-xs text-slate-500">Loading rank race…</span>
      </div>
    );
  }
  if (error) {
    return <div className="rounded-2xl bg-rose-50 border border-rose-200 p-6 text-xs text-rose-700">{error}</div>;
  }
  if (!data || data.tracks.length === 0 || data.days.length < 2) {
    return (
      <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-200 p-12 text-center">
        <Activity className="w-6 h-6 text-slate-300 inline mb-2" />
        <div className="text-xs text-slate-500 font-semibold">Not enough scrape history yet</div>
        <div className="text-[11px] text-slate-400 mt-1">
          The rank race needs at least 2 scrapes of this category to plot movement.
          {data && data.days.length === 1 && <span className="block mt-0.5">Currently have just {data.days.length} day of data.</span>}
        </div>
      </div>
    );
  }

  // ─── Trim to the LAST 2 scrapes only ─────────────────────────────
  // Users want the chart to read as "movement between the previous run
  // and the current run" — showing 3+ scrapes makes it feel cluttered and
  // hides the most useful signal (what changed in the latest crawl).
  //
  // We slice `days` + each track's `series` to the tail-2 window and
  // derive per-track state flags from the two endpoints:
  //   • is_new     = wasn't in top-N previously, is now (entered chart)
  //   • is_dropped = was in top-N previously, isn't now (exited chart)
  //   • prev_rank_top / curr_rank_top = top-N rank at each endpoint (or null)
  //   • prev_rank_full / curr_rank_full = actual rank incl. outside top-N,
  //       pulled from `rank_uncapped` (backend). Powers the "↑ from #14" /
  //       "↓ to #22" arrows on the leaderboard.
  //
  // Tracks with no ranked point at either endpoint are dropped so we
  // don't render ghost rows.
  const rawXs = data.days;
  const SLICE = 2;
  const startIdx = Math.max(0, rawXs.length - SLICE);
  const xs = rawXs.slice(startIdx);
  const allTracks = (data.tracks || [])
    .map((t) => {
      const series = (t.series || []).slice(startIdx);
      const prevPt = series.length >= 2 ? series[0] : null;
      const currPt = series[series.length - 1] || null;
      const prev_rank_top = prevPt?.rank ?? null;
      const curr_rank_top = currPt?.rank ?? null;
      const prev_rank_full = prevPt?.rank_uncapped ?? null;
      const curr_rank_full = currPt?.rank_uncapped ?? null;
      const is_new = series.length >= 2 ? (prev_rank_top == null && curr_rank_top != null) : Boolean(t.is_new);
      const is_dropped = series.length >= 2 && prev_rank_top != null && curr_rank_top == null;
      return {
        ...t,
        series,
        current_rank: curr_rank_top,
        prev_rank_top,
        curr_rank_top,
        prev_rank_full,
        curr_rank_full,
        is_new,
        is_dropped,
      };
    })
    .filter((t) => t.prev_rank_top != null || t.curr_rank_top != null);
  const tracks = allTracks;
  const top_n = 10;
  // Chart dims
  const W = 980, H = 360, PAD_L = 50, PAD_R = 240, PAD_T = 24, PAD_B = 40;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const xScale = (i) => PAD_L + (xs.length === 1 ? innerW / 2 : (i / (xs.length - 1)) * innerW);
  const yScale = (rank) => PAD_T + ((rank - 1) / (top_n - 1)) * innerH;

  // Y-position representing "outside top 10" — used to render dashed
  // entry/exit trajectories for NEW arrivals and DROPPED brands. Sits
  // just below the last visible tick so the eye reads it as "off-chart".
  const OFF_CHART_Y = PAD_T + innerH + 22;

  // Build SVG path per track — always TWO points now (prev + curr).
  // A null rank at either endpoint means "outside top 10", which we
  // render as OFF_CHART_Y so entry/exit lines have a visible slope.
  const buildPath = (series) => {
    if (!series || series.length === 0) return "";
    const pts = series.map((p, i) => ({
      x: xScale(i),
      y: p.rank != null ? yScale(p.rank) : OFF_CHART_Y,
    }));
    if (pts.length === 1) return `M ${pts[0].x},${pts[0].y} L ${pts[0].x},${pts[0].y}`;
    let d = `M ${pts[0].x},${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const p0 = pts[i - 1], p1 = pts[i];
      const cx = (p0.x + p1.x) / 2;
      d += ` C ${cx},${p0.y} ${cx},${p1.y} ${p1.x},${p1.y}`;
    }
    return d;
  };

  const fmtDay = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  // X-tick labels — at most 6 visible
  const tickEvery = Math.max(1, Math.ceil(xs.length / 6));
  const xTicks = xs.map((d, i) => ({ d, i })).filter(t => t.i % tickEvery === 0 || t.i === xs.length - 1);

  // Map asin → palette color
  const colorByAsin = {};
  tracks.forEach((t, idx) => { colorByAsin[t.asin] = RANK_PALETTE[idx % RANK_PALETTE.length]; });

  // Hover index
  const hoverIdx = hoverX != null
    ? Math.max(0, Math.min(xs.length - 1, Math.round(hoverX * (xs.length - 1))))
    : null;

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const xPx = e.clientX - rect.left;
    const ratio = (xPx - PAD_L * (rect.width / W)) / ((W - PAD_L - PAD_R) * (rect.width / W));
    if (ratio < 0 || ratio > 1) { setHoverX(null); return; }
    setHoverX(ratio);
  };

  return (
    <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 p-5 sm:p-7 text-white relative overflow-hidden" data-testid="dd-rank-race">
      {/* Decorative glow */}
      <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-indigo-500/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-pink-500/10 blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-5">
          <div>
            <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-indigo-300/80">Rank Race</span>
            <div className="flex items-center gap-2 mt-0.5">
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight">
                Top {top_n} bestseller movement
              </h3>
              {/* Info tooltip — explains the 2-scrape plotting logic to
                  first-time viewers. Uses native `title` so it works on
                  both hover (desktop) and tap-hold (mobile) without an
                  extra popover component. */}
              <span
                className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/10 text-slate-300 hover:bg-white/20 hover:text-white cursor-help transition-colors"
                data-testid="dd-rank-race-info"
                title={
                  "How this chart is plotted:\n\n" +
                  `• Only the LAST 2 scrapes are shown so you can read the change at a glance (previous run → latest run).\n` +
                  `• Solid line = brand stayed in top ${top_n}; slope shows the climb or fall.\n` +
                  `• Dashed rising line + NEW badge = brand entered top ${top_n} from outside (leaderboard shows "from #X" using its actual rank).\n` +
                  `• Dashed falling line + DROPPED badge = brand exited top ${top_n}; leaderboard shows "to #Y" (its new rank outside).\n` +
                  `• "held" = brand kept the exact same rank across both runs.\n` +
                  `• Click any line or card to open the product details.`
                }
              >
                <Info className="w-2.5 h-2.5" />
              </span>
            </div>
            <p className="text-[11px] text-slate-300 mt-1">
              {tracks.filter(t => t.current_rank != null).length} ASIN{tracks.filter(t => t.current_rank != null).length === 1 ? "" : "s"} currently in top {top_n} · movement between the last {xs.length === 1 ? "run" : `${xs.length} runs`}
            </p>
            {/* Scrape timestamps — show first → last in the window */}
            {xs.length >= 1 && (() => {
              const fmt = (iso) => {
                if (!iso) return "—";
                const d = new Date(iso);
                return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
              };
              return (
                <p className="text-[10px] text-slate-400 mt-1 font-mono tabular-nums" data-testid="dd-rank-race-timestamps">
                  Movement: <span className="text-slate-200">{fmt(xs[0])}</span>
                  <span className="mx-1.5 text-slate-500">→</span>
                  <span className="text-slate-200">{fmt(xs[xs.length - 1])}</span>
                </p>
              );
            })()}
          </div>

          {/* Right-side — view toggle (Bump | Slope). Users wanted to keep
              the bump chart as default but have a cleaner side-by-side
              view available on demand. State persists via useState so
              reopening the deep dive remembers the choice. */}
          <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-full p-0.5" data-testid="dd-rank-race-view-toggle">
            <button
              type="button"
              onClick={() => setViewMode("bump")}
              className={`px-3 h-7 rounded-full text-[10.5px] font-bold tracking-wide transition-all ${
                viewMode === "bump"
                  ? "bg-indigo-500 text-white shadow-md shadow-indigo-500/30"
                  : "text-slate-400 hover:text-white"
              }`}
              data-testid="dd-rank-race-view-bump"
            >
              Bump chart
            </button>
            <button
              type="button"
              onClick={() => setViewMode("slope")}
              className={`px-3 h-7 rounded-full text-[10.5px] font-bold tracking-wide transition-all ${
                viewMode === "slope"
                  ? "bg-indigo-500 text-white shadow-md shadow-indigo-500/30"
                  : "text-slate-400 hover:text-white"
              }`}
              data-testid="dd-rank-race-view-slope"
            >
              Slope chart
            </button>
          </div>
        </div>

        {/* Chart */}
        {viewMode === "bump" && (
        <div className="relative">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${W} ${H}`}
            className="w-full h-auto cursor-crosshair"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoverX(null)}
            data-testid="dd-rank-race-svg"
          >
            {/* Horizontal grid lines — one per rank position */}
            {Array.from({ length: top_n }).map((_, i) => {
              const y = yScale(i + 1);
              return (
                <g key={i}>
                  <line x1={PAD_L} x2={W - PAD_R} y1={y} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth={1} />
                  <text x={PAD_L - 10} y={y + 4} textAnchor="end" fontSize="11" fill="rgba(255,255,255,0.7)" fontFamily="monospace" fontWeight="700">
                    {`#${i + 1}`}
                  </text>
                </g>
              );
            })}

            {/* X-axis labels */}
            {xTicks.map(t => (
              <text key={t.d} x={xScale(t.i)} y={H - PAD_B + 18} textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.45)" fontFamily="monospace">
                {fmtDay(t.d)}
              </text>
            ))}

            {/* Hover guide line */}
            {hoverIdx != null && (
              <line x1={xScale(hoverIdx)} x2={xScale(hoverIdx)} y1={PAD_T} y2={H - PAD_B} stroke="rgba(255,255,255,0.3)" strokeWidth={1} strokeDasharray="2 3" />
            )}

            {/* Track lines — dashed if the ASIN entered (NEW) or exited
                (DROPPED) the top-N in the latest run; solid otherwise. */}
            {tracks.map((t) => {
              const color = colorByAsin[t.asin];
              const dimmed = selectedAsin && selectedAsin !== t.asin;
              const d = buildPath(t.series);
              const isPartial = t.is_new || t.is_dropped;
              return (
                <g
                  key={t.asin}
                  style={{
                    opacity: dimmed ? 0.18 : 1,
                    transition: "opacity 0.2s",
                    cursor: "pointer",
                  }}
                  onClick={() => setDetailAsin(t.asin)}
                  data-testid={`dd-rank-race-line-${t.asin}`}
                >
                  <path d={d} fill="none" stroke="transparent" strokeWidth={14} strokeLinecap="round" />
                  <path d={d} fill="none" stroke={color} strokeWidth={6} strokeOpacity={0.25} strokeLinecap="round" strokeLinejoin="round"
                        style={{ filter: "blur(2px)", pointerEvents: "none" }} />
                  <path
                    d={d} fill="none" stroke={color}
                    strokeWidth={2.5}
                    strokeLinecap="round" strokeLinejoin="round"
                    strokeDasharray={isPartial ? "5 5" : "2000"}
                    strokeDashoffset={isPartial ? 0 : (animDone ? 0 : 2000)}
                    style={{ transition: "stroke-dashoffset 1.2s ease-out", pointerEvents: "none" }}
                  />
                  {t.series.map((p, i) => p.rank != null && (
                    <circle
                      key={i}
                      cx={xScale(i)}
                      cy={yScale(p.rank)}
                      r={hoverIdx === i ? 4.5 : 3}
                      fill={hoverIdx === i ? "white" : color}
                      stroke={color}
                      strokeWidth={hoverIdx === i ? 2 : 0}
                      style={{ transition: "all 0.15s", pointerEvents: "none" }}
                    />
                  ))}
                  {/* Off-chart end-cap for NEW entries: small hollow ring at
                      x=prev, y=off-chart so the eye locks onto the origin. */}
                  {t.is_new && (
                    <circle
                      cx={xScale(0)} cy={OFF_CHART_Y} r={3}
                      fill="none" stroke={color} strokeWidth={1.5} strokeOpacity={0.7}
                      style={{ pointerEvents: "none" }}
                    />
                  )}
                  {/* Off-chart end-cap for DROPPED exits at x=curr,off-chart */}
                  {t.is_dropped && (
                    <circle
                      cx={xScale(t.series.length - 1)} cy={OFF_CHART_Y} r={3}
                      fill="none" stroke={color} strokeWidth={1.5} strokeOpacity={0.7}
                      style={{ pointerEvents: "none" }}
                    />
                  )}
                </g>
              );
            })}

            {/* Left-edge labels — ONLY for ASINs that just DROPPED OUT of
                top-N in the latest run. Mirror of the right-edge treatment
                so exits stay as visible as entries. Shows "was #X · DROPPED
                → #curr" so the user knows both the prior position and where
                the ASIN currently sits (outside top-N). */}
            {(() => {
              if (!tracks.some(t => t.is_dropped)) return null;
              const ROW_H = 22;
              const rows = tracks
                .filter(t => t.is_dropped)
                .map(t => ({ t, yIdeal: yScale(t.prev_rank_top), x: xScale(0) }))
                .sort((a, b) => a.yIdeal - b.yIdeal);
              let prevY = -Infinity;
              rows.forEach(r => {
                let y = r.yIdeal;
                if (y - prevY < ROW_H) y = prevY + ROW_H;
                r.yFinal = y;
                prevY = y;
              });
              return rows.map(({ t, x, yFinal }) => {
                const color = colorByAsin[t.asin];
                const dimmed = selectedAsin && selectedAsin !== t.asin;
                const labelText = t.brand || t.asin;
                const truncated = labelText.length > 12 ? labelText.slice(0, 10) + "…" : labelText;
                const chipW = 26;
                // The left-side label sits IN the reserved PAD_L strip,
                // right-anchored so it never overlaps the axis ticks.
                const chipX = 2;
                const droppedX = chipX + chipW + 4;
                const droppedW = 46;
                const brandX = droppedX + droppedW + 4;
                return (
                  <g key={`dropped-${t.asin}`} style={{ opacity: dimmed ? 0.18 : 1, transition: "opacity 0.2s" }}>
                    <rect x={chipX} y={yFinal - 8} width={chipW} height={16} rx={4} fill={color} fillOpacity={0.22} stroke={color} strokeOpacity={0.6} strokeWidth={1} />
                    <text x={chipX + chipW / 2} y={yFinal + 4} textAnchor="middle" fontSize="10" fontWeight="800" fill="white" fontFamily="monospace">
                      #{t.prev_rank_top}
                    </text>
                    <rect x={droppedX} y={yFinal - 8} width={droppedW} height={16} rx={4} fill="#ef4444" fillOpacity={0.95} />
                    <text x={droppedX + droppedW / 2} y={yFinal + 4} textAnchor="middle" fontSize="8" fontWeight="900" fill="white" letterSpacing="0.8">
                      DROPPED
                    </text>
                    <text x={brandX} y={yFinal + 4} fontSize="10.5" fill={color} fontWeight="700">
                      {truncated}
                    </text>
                  </g>
                );
              });
            })()}

            {/* Right-edge labels — ONLY for ASINs currently in top-N (one label
                per rank, no duplicates). Dropped-out ASINs get a "DROPPED"
                tag on the LEFT edge instead (see block above). */}
            {(() => {
              const ROW_H = 22;
              const rows = [];
              tracks.forEach((t) => {
                if (t.current_rank == null) return;          // dropped → no right-edge label
                const lastWithRank = [...t.series].reverse().find(p => p.rank != null);
                if (!lastWithRank) return;
                const lastIdx = t.series.lastIndexOf(t.series.find(p => p.day === lastWithRank.day));
                rows.push({
                  t, lastWithRank, lastIdx,
                  yIdeal: yScale(lastWithRank.rank),
                  x: xScale(lastIdx),
                });
              });
              rows.sort((a, b) => a.yIdeal - b.yIdeal);
              let prevY = -Infinity;
              rows.forEach(r => {
                let y = r.yIdeal;
                if (y - prevY < ROW_H) y = prevY + ROW_H;
                r.yFinal = y;
                prevY = y;
              });

              return rows.map(({ t, lastWithRank, x, yIdeal, yFinal }) => {
                const color = colorByAsin[t.asin];
                const dimmed = selectedAsin && selectedAsin !== t.asin;
                const labelText = t.brand || t.asin;
                const truncated = labelText.length > 15 ? labelText.slice(0, 13) + "…" : labelText;
                const rankStr = `#${lastWithRank.rank}`;
                const chipW = rankStr.length === 2 ? 26 : 32;
                const chipX = W - PAD_R + 8;
                const newPillX = chipX + chipW + 4;
                const newPillW = t.is_new ? 32 : 0;
                const brandX = chipX + chipW + (t.is_new ? newPillW + 8 : 6);
                return (
                  <g key={`label-${t.asin}`} style={{ opacity: dimmed ? 0.18 : 1, transition: "opacity 0.2s" }}>
                    <path
                      d={`M ${x + 4} ${yIdeal} L ${chipX - 6} ${yIdeal} L ${chipX - 2} ${yFinal}`}
                      fill="none" stroke={color} strokeWidth={1} strokeDasharray="2 2" strokeOpacity={0.5}
                    />
                    <rect x={chipX} y={yFinal - 8} width={chipW} height={16} rx={4} fill={color} fillOpacity={0.22} stroke={color} strokeOpacity={0.6} strokeWidth={1} />
                    <text x={chipX + chipW / 2} y={yFinal + 4} textAnchor="middle" fontSize="11" fontWeight="800" fill="white" fontFamily="monospace">
                      {rankStr}
                    </text>
                    {t.is_new && (
                      <>
                        <rect x={newPillX} y={yFinal - 8} width={newPillW} height={16} rx={4} fill="#22c55e" fillOpacity={0.95} />
                        <text x={newPillX + newPillW / 2} y={yFinal + 4} textAnchor="middle" fontSize="9" fontWeight="900" fill="white" letterSpacing="1">
                          NEW
                        </text>
                      </>
                    )}
                    <text x={brandX} y={yFinal + 4} fontSize="11.5" fill={color} fontWeight="700">
                      {truncated}
                    </text>
                  </g>
                );
              });
            })()}

            {/* (FELL OFF inline annotations removed — kept the chart minimal.
                Dropped ASINs simply don't appear with right-edge labels.) */}

            {/* Hover tooltip */}
            {hoverIdx != null && (
              <g>
                <rect x={xScale(hoverIdx) - 36} y={H - PAD_B + 4} width={72} height={16} rx={3} fill="rgba(99,102,241,0.9)" />
                <text x={xScale(hoverIdx)} y={H - PAD_B + 14} textAnchor="middle" fontSize="9" fill="white" fontFamily="monospace" fontWeight="600">
                  {fmtDay(xs[hoverIdx])}
                </text>
              </g>
            )}
          </svg>
          {/* Legend */}
          <div className="mt-2 flex items-center justify-end gap-3 text-[9.5px] text-slate-400 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-2 rounded-sm bg-emerald-500" />
              <span><b className="text-emerald-300">NEW</b> · entered top {top_n}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-2 rounded-sm bg-rose-500" />
              <span><b className="text-rose-300">DROPPED</b> · exited top {top_n}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <svg width="22" height="4"><line x1="0" y1="2" x2="22" y2="2" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" /></svg>
              <span>dashed = partial (entered/exited)</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-0.5 rounded-full bg-white/60" />
              <span>solid = stayed in top {top_n} · click to view product</span>
            </span>
          </div>
        </div>
        )}

        {/* ─── Slope chart (two-rail) ─────────────────────────────────
            Alternative view toggled from the header. Classic slope-chart
            layout: two vertical rails labelled Previous and Latest, each
            listing the top-N brands at that scrape. Lines connect the
            same brand across runs — crossings are intentional and show
            "who overtook whom". Green = climbed · red = fell · dashed
            emerald/rose = NEW/DROPPED. Click a brand label to drill
            into its ASINs. */}
        {viewMode === "slope" && (() => {
          // Partition tracks for the two rails:
          //   prevList — anything with a prev rank (held, climbed, fell, dropped)
          //   currList — anything with a curr rank (held, climbed, fell, NEW)
          const prevList = [...tracks]
            .filter(t => t.prev_rank_top != null)
            .sort((a, b) => a.prev_rank_top - b.prev_rank_top);
          const currList = [...tracks]
            .filter(t => t.curr_rank_top != null)
            .sort((a, b) => a.curr_rank_top - b.curr_rank_top);
          // Also render NEW arrivals + DROPPED exits as off-rail dashed
          // tails anchored to a sentinel slot just past the last rank.
          const newList = tracks.filter(t => t.is_new);
          const droppedList = tracks.filter(t => t.is_dropped);
          if (prevList.length === 0 && currList.length === 0) {
            return (
              <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-10 text-center text-xs text-slate-400">
                No ASINs to plot in the slope view.
              </div>
            );
          }
          // Chart dims — rail positions, row height follows top_n so both
          // rails line up at the same y-positions by rank slot.
          const SW = 1100;
          const ROW_H = 42;
          const TOP_PAD = 48;
          const BOT_PAD = 20;
          const RAIL_LEFT_X  = 320;
          const RAIL_RIGHT_X = SW - 320;
          const SH = TOP_PAD + top_n * ROW_H + BOT_PAD;
          // y-slot for rank r (1..top_n). OFF rail (r = top_n + 1) sits
          // one extra row below — used for NEW/DROPPED endpoints.
          const yForRank = (r) => TOP_PAD + (r - 0.5) * ROW_H;
          const OFF_SLOT_Y = TOP_PAD + (top_n + 0.5) * ROW_H;
          // Rail header colors matching reference image
          return (
            <div data-testid="dd-rank-race-slope">
              <div className="rounded-xl bg-slate-950/40 ring-1 ring-white/5 p-4 sm:p-6 overflow-x-auto">
                <svg viewBox={`0 0 ${SW} ${SH + 10}`} className="w-full h-auto" style={{ minWidth: 640 }}>
                  {/* Rail headers */}
                  <text x={RAIL_LEFT_X} y={24} textAnchor="middle" fontSize="14" fontWeight="700" fill="#94a3b8" style={{ letterSpacing: "-0.01em" }}>Previous</text>
                  <text x={RAIL_RIGHT_X} y={24} textAnchor="middle" fontSize="14" fontWeight="700" fill="#f1f5f9" style={{ letterSpacing: "-0.01em" }}>Latest</text>

                  {/* Faint horizontal gridlines at each rank slot */}
                  {Array.from({ length: top_n }).map((_, i) => {
                    const y = yForRank(i + 1);
                    return (
                      <line key={`g-${i}`} x1={RAIL_LEFT_X - 90} y1={y} x2={RAIL_RIGHT_X + 90} y2={y} stroke="rgba(148, 163, 184, 0.06)" strokeWidth="1" />
                    );
                  })}

                  {/* Connector lines — drawn FIRST so dots render on top */}
                  {tracks.map((t) => {
                    const prevIn = t.prev_rank_top != null;
                    const currIn = t.curr_rank_top != null;
                    if (!prevIn && !currIn) return null;
                    const y1 = prevIn ? yForRank(t.prev_rank_top) : OFF_SLOT_Y;
                    const y2 = currIn ? yForRank(t.curr_rank_top) : OFF_SLOT_Y;
                    const delta = (prevIn && currIn) ? (t.prev_rank_top - t.curr_rank_top) : null;
                    let color = "#64748b"; let dash = null;
                    if (t.is_new)             { color = "#22c55e"; dash = "6 4"; }
                    else if (t.is_dropped)    { color = "#ef4444"; dash = "6 4"; }
                    else if (delta != null && delta > 0) color = "#22c55e";
                    else if (delta != null && delta < 0) color = "#ef4444";
                    const brandLabel = t.brand || t.asin || "—";
                    const titleText = t.is_new
                      ? `${brandLabel} · NEW · entered at #${t.curr_rank_top} (previously outside top ${top_n}${t.prev_rank_full ? `, at #${t.prev_rank_full}` : ""}) · click to see ASINs`
                      : t.is_dropped
                        ? `${brandLabel} · DROPPED · was #${t.prev_rank_top}, now ${t.curr_rank_full ? `#${t.curr_rank_full}` : "outside"} · click to see ASINs`
                        : (delta != null
                          ? `${brandLabel} · ${delta > 0 ? "climbed" : (delta < 0 ? "fell" : "held")} ${delta !== 0 ? Math.abs(delta) : ""} · #${t.prev_rank_top} → #${t.curr_rank_top} · click to see ASINs`
                          : `${brandLabel} · click to see ASINs`);
                    return (
                      <g
                        key={`line-${t.asin}`}
                        className="cursor-pointer group/slope"
                        onClick={() => setBrandDrill(brandLabel)}
                        data-testid={`dd-rank-race-slope-line-${t.asin}`}
                      >
                        {/* Invisible wide hit area — makes the slope easy
                            to click without a pixel-perfect aim. */}
                        <line
                          x1={RAIL_LEFT_X + 6}
                          y1={y1}
                          x2={RAIL_RIGHT_X - 6}
                          y2={y2}
                          stroke="transparent"
                          strokeWidth="18"
                          strokeLinecap="round"
                        />
                        {/* Visible slope line */}
                        <line
                          x1={RAIL_LEFT_X + 6}
                          y1={y1}
                          x2={RAIL_RIGHT_X - 6}
                          y2={y2}
                          stroke={color}
                          strokeWidth="2.25"
                          strokeDasharray={dash || undefined}
                          strokeLinecap="round"
                          opacity={animDone ? 0.9 : 0}
                          className="group-hover/slope:opacity-100 transition-opacity"
                          style={{ transition: "opacity 500ms ease" }}
                        />
                        <title>{titleText}</title>
                      </g>
                    );
                  })}

                  {/* Left rail (Previous) — labels + dots */}
                  {prevList.map((t) => {
                    const y = yForRank(t.prev_rank_top);
                    const delta = (t.prev_rank_top != null && t.curr_rank_top != null) ? (t.prev_rank_top - t.curr_rank_top) : null;
                    const bigClimber = (delta != null && delta >= 2) || t.is_new;
                    const willDrop = t.is_dropped;
                    const brandLabel = t.brand || t.asin || "—";
                    const labelFill = willDrop ? "#fda4af" : (bigClimber ? "#60a5fa" : "#cbd5e1");
                    let dotFill = "#94a3b8";
                    if (willDrop)                 dotFill = "#ef4444";
                    else if (delta != null && delta > 0) dotFill = "#22c55e";
                    else if (delta != null && delta < 0) dotFill = "#ef4444";
                    return (
                      <g
                        key={`prev-${t.asin}`}
                        className="cursor-pointer"
                        onClick={() => setBrandDrill(brandLabel)}
                        data-testid={`dd-rank-race-slope-prev-${t.asin}`}
                      >
                        <text
                          x={RAIL_LEFT_X - 14}
                          y={y + 4}
                          textAnchor="end"
                          fontSize="14"
                          fontWeight={bigClimber ? 700 : 600}
                          fill={labelFill}
                          style={{ letterSpacing: "-0.015em" }}
                        >
                          {brandLabel} <tspan fill="#64748b" fontSize="12" fontWeight="500">{t.prev_rank_top}</tspan>
                        </text>
                        <circle cx={RAIL_LEFT_X} cy={y} r={5.5} fill="#0f172a" stroke={dotFill} strokeWidth="2.25" />
                      </g>
                    );
                  })}

                  {/* Right rail (Latest) — labels + dots */}
                  {currList.map((t) => {
                    const y = yForRank(t.curr_rank_top);
                    const delta = (t.prev_rank_top != null && t.curr_rank_top != null) ? (t.prev_rank_top - t.curr_rank_top) : null;
                    const bigClimber = (delta != null && delta >= 2) || t.is_new;
                    const brandLabel = t.brand || t.asin || "—";
                    const labelFill = bigClimber ? "#60a5fa" : (delta != null && delta < 0 ? "#f8fafc" : "#f8fafc");
                    let dotFill = "#94a3b8";
                    if (t.is_new)                 dotFill = "#22c55e";
                    else if (delta != null && delta > 0) dotFill = "#22c55e";
                    else if (delta != null && delta < 0) dotFill = "#ef4444";
                    return (
                      <g
                        key={`curr-${t.asin}`}
                        className="cursor-pointer"
                        onClick={() => setBrandDrill(brandLabel)}
                        data-testid={`dd-rank-race-slope-curr-${t.asin}`}
                      >
                        <circle cx={RAIL_RIGHT_X} cy={y} r={6} fill={dotFill} stroke="#f8fafc" strokeWidth="1.75" />
                        <text
                          x={RAIL_RIGHT_X + 14}
                          y={y + 4}
                          fontSize="14"
                          fontWeight={bigClimber ? 700 : 600}
                          fill={labelFill}
                          style={{ letterSpacing: "-0.015em" }}
                        >
                          <tspan fill="#64748b" fontSize="12" fontWeight="500">{t.curr_rank_top}</tspan> {brandLabel}
                        </text>
                      </g>
                    );
                  })}

                  {/* NEW arrivals — anchored to OFF slot on the LEFT rail */}
                  {newList.map((t) => {
                    const brandLabel = t.brand || t.asin || "—";
                    return (
                      <g
                        key={`new-left-${t.asin}`}
                        className="cursor-pointer"
                        onClick={() => setBrandDrill(brandLabel)}
                      >
                        <circle cx={RAIL_LEFT_X} cy={OFF_SLOT_Y} r={4.5} fill="#0f172a" stroke="#22c55e" strokeWidth="2" opacity="0.55" />
                      </g>
                    );
                  })}
                  {/* DROPPED exits — anchored to OFF slot on the RIGHT rail */}
                  {droppedList.map((t) => {
                    const brandLabel = t.brand || t.asin || "—";
                    return (
                      <g
                        key={`drop-right-${t.asin}`}
                        className="cursor-pointer"
                        onClick={() => setBrandDrill(brandLabel)}
                      >
                        <circle cx={RAIL_RIGHT_X} cy={OFF_SLOT_Y} r={4.5} fill="#ef4444" stroke="#f8fafc" strokeWidth="1.5" opacity="0.55" />
                      </g>
                    );
                  })}
                  {/* OFF-rail label */}
                  {(newList.length > 0 || droppedList.length > 0) && (
                    <>
                      <text x={RAIL_LEFT_X - 14} y={OFF_SLOT_Y + 4} textAnchor="end" fontSize="12" fontWeight="600" fill="#64748b" style={{ letterSpacing: "-0.01em" }}>
                        {newList.length > 0 ? `+${newList.length} new ↗` : ""}
                      </text>
                      <text x={RAIL_RIGHT_X + 14} y={OFF_SLOT_Y + 4} fontSize="12" fontWeight="600" fill="#64748b" style={{ letterSpacing: "-0.01em" }}>
                        {droppedList.length > 0 ? `↘ ${droppedList.length} dropped` : ""}
                      </text>
                    </>
                  )}
                </svg>
              </div>
              <div className="mt-3 flex items-center justify-between gap-4 text-[10px] text-slate-400 flex-wrap">
                <span className="italic">Two runs side by side · crossing lines show who overtook whom · click any brand to drill into its ASINs.</span>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-emerald-500 rounded-full" />climbed</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-rose-500 rounded-full" />fell</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-slate-500 rounded-full" />held</span>
                  <span className="inline-flex items-center gap-1">
                    <svg width="18" height="4"><line x1="0" y1="2" x2="18" y2="2" stroke="#22c55e" strokeWidth="1.6" strokeDasharray="3 3" /></svg>
                    <span>NEW / dropped</span>
                  </span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Leader cards — click to highlight a track */}
        <div className="mt-5 flex items-center justify-between gap-3 mb-2 flex-wrap">
          <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-indigo-300/80">
            {selectedAsin ? "Highlighted brand" : "Leaderboard"}
          </span>
          {selectedAsin && (
            <button
              type="button"
              onClick={() => setSelectedAsin(null)}
              className="px-3 py-1 text-[10px] font-semibold rounded-full bg-white/15 border border-white/30 text-white hover:bg-white/25 transition-all inline-flex items-center gap-1.5"
              data-testid="dd-rank-race-all-brands"
            >
              <X className="w-3 h-3" />
              Show all brands
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {tracks.filter(t => t.current_rank != null).slice(0, 10).map((t) => {
            const isSelected = selectedAsin === t.asin;
            const color = colorByAsin[t.asin];
            // Movement arrow — uses uncapped ranks so "↑ from #14" works
            // for ASINs that were outside top-N in the previous run.
            const prev = t.prev_rank_full;
            const curr = t.curr_rank_full;
            let moveEl = null;
            if (t.is_new) {
              moveEl = (
                <span className="inline-flex items-center gap-0.5 text-emerald-400 text-[9px] font-semibold" title={`Entered top ${top_n} — was ${prev != null ? '#' + prev : 'outside'}`}>
                  <ArrowUp className="w-2.5 h-2.5" />{prev != null ? `from #${prev}` : `from outside`}
                </span>
              );
            } else if (prev != null && curr != null && prev !== curr) {
              const climbed = curr < prev;
              const delta = Math.abs(curr - prev);
              moveEl = (
                <span
                  className={`inline-flex items-center gap-0.5 text-[9px] font-semibold ${climbed ? "text-emerald-400" : "text-rose-400"}`}
                  title={`${climbed ? "Climbed" : "Fell"} ${delta} spot${delta === 1 ? "" : "s"} · was #${prev}`}
                >
                  {climbed ? <ArrowUp className="w-2.5 h-2.5" /> : <ArrowDown className="w-2.5 h-2.5" />}
                  from #{prev}
                </span>
              );
            } else if (prev != null && curr != null && prev === curr) {
              moveEl = (
                <span className="inline-flex items-center gap-0.5 text-slate-400 text-[9px] font-semibold" title="No rank change since last run">
                  <Minus className="w-2.5 h-2.5" />held
                </span>
              );
            }
            return (
              <button
                key={t.asin}
                type="button"
                onClick={() => setSelectedAsin(isSelected ? null : t.asin)}
                className={`text-left p-2.5 rounded-lg border transition-all ${
                  isSelected
                    ? "bg-white/15 border-white/40 shadow-lg ring-2 ring-white/30"
                    : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                }`}
                data-testid={`dd-rank-race-card-${t.asin}`}
                title={t.title}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
                  <span className="text-[10px] font-bold text-white truncate flex-1" title={t.brand || t.asin}>
                    {t.brand || t.asin}
                  </span>
                  {t.is_new && (
                    <span className="px-1.5 py-0.5 rounded text-[8px] font-black tracking-wider text-white bg-emerald-500 shrink-0">
                      NEW
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-bold font-mono tabular-nums" style={{ color }}>
                    #{t.current_rank}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-0.5 gap-1">
                  <div className="text-[9px] text-slate-400">
                    peak #{t.peak_rank}
                  </div>
                  {moveEl}
                </div>
              </button>
            );
          })}
        </div>

        {/* ─── DROPPED brands row ─────────────────────────────────
            Mirror the current top-10 grid for ASINs that just fell OUT
            of top-N in the latest run — same card look, red trim, "was #X
            → now #Y" so the exit story is as visible as the entry story. */}
        {(() => {
          const dropped = tracks.filter(t => t.is_dropped);
          if (dropped.length === 0) return null;
          return (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-rose-300/80">
                  Just dropped out of top {top_n}
                </span>
                <span className="text-[10px] text-slate-400">· {dropped.length} brand{dropped.length === 1 ? "" : "s"}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {dropped.map((t) => {
                  const color = colorByAsin[t.asin];
                  const isSelected = selectedAsin === t.asin;
                  return (
                    <button
                      key={t.asin}
                      type="button"
                      onClick={() => setSelectedAsin(isSelected ? null : t.asin)}
                      className={`text-left p-2.5 rounded-lg border transition-all ${
                        isSelected
                          ? "bg-rose-500/15 border-rose-400/60 shadow-lg ring-2 ring-rose-400/30"
                          : "bg-rose-500/5 border-rose-400/20 hover:bg-rose-500/10 hover:border-rose-400/40"
                      }`}
                      data-testid={`dd-rank-race-dropped-card-${t.asin}`}
                      title={t.title}
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
                        <span className="text-[10px] font-bold text-white truncate flex-1" title={t.brand || t.asin}>
                          {t.brand || t.asin}
                        </span>
                        <span className="px-1.5 py-0.5 rounded text-[8px] font-black tracking-wider text-white bg-rose-500 shrink-0">
                          DROPPED
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg font-bold font-mono tabular-nums text-slate-400 line-through">
                          #{t.prev_rank_top}
                        </span>
                        <span className="inline-flex items-center gap-0.5 text-rose-400 text-[10px] font-semibold">
                          <ArrowDown className="w-3 h-3" />
                          {t.curr_rank_full != null ? `to #${t.curr_rank_full}` : "outside"}
                        </span>
                      </div>
                      <div className="text-[9px] text-slate-400 mt-0.5">peak #{t.peak_rank}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Product detail modal — opens when a chart line is clicked */}
      {detailAsin && (() => {
        const t = tracks.find(x => x.asin === detailAsin);
        if (!t) return null;
        const lastWithRank = [...t.series].reverse().find(p => p.rank != null);
        const ranksByDay = t.series.filter(p => p.rank != null);
        return (
          <div
            className="fixed inset-0 z-[65] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setDetailAsin(null)}
            data-testid="dd-rank-race-detail-modal"
          >
            <div
              className="bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 w-full max-w-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="px-5 py-4 border-b border-slate-100 flex items-start gap-3"
                style={{ background: `linear-gradient(135deg, ${colorByAsin[t.asin]}15 0%, transparent 100%)` }}
              >
                {t.image && (
                  t.url
                    ? <a href={t.url} target="_blank" rel="noreferrer" className="shrink-0 group" title="Open on Amazon">
                        <img src={t.image} alt="" className="w-14 h-14 rounded-lg object-cover bg-slate-100 ring-1 ring-slate-200 group-hover:ring-indigo-400 transition-all" />
                      </a>
                    : <img src={t.image} alt="" className="w-14 h-14 rounded-lg object-cover bg-slate-100 shrink-0 ring-1 ring-slate-200" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: colorByAsin[t.asin] }}>
                    Top {top_n} contender
                  </div>
                  {t.url ? (
                    <a href={t.url} target="_blank" rel="noreferrer" className="block text-base font-bold text-slate-900 leading-tight mt-0.5 hover:text-indigo-600 transition-colors group" title={`${t.title} — open on Amazon`}>
                      {t.title || t.asin}
                      <ExternalLink className="inline-block w-3 h-3 ml-1 opacity-60 group-hover:opacity-100" />
                    </a>
                  ) : (
                    <div className="text-base font-bold text-slate-900 leading-tight mt-0.5" title={t.title}>{t.title || t.asin}</div>
                  )}
                  <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                    {t.brand || "—"} ·{" "}
                    {t.url ? (
                      <a href={t.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline font-semibold">{t.asin}</a>
                    ) : (
                      <span>{t.asin}</span>
                    )}
                  </div>
                </div>
                <button type="button" onClick={() => setDetailAsin(null)} className="shrink-0 w-7 h-7 rounded-full bg-white/80 hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors" data-testid="dd-rank-race-detail-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="px-5 py-4 space-y-4">
                {/* Removed Velocity stat card — only Current rank + Peak rank now */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                    <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Current rank</div>
                    <div className="text-xl font-bold font-mono tabular-nums text-slate-900 mt-0.5">
                      #{t.current_rank ?? "—"}
                    </div>
                  </div>
                  <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                    <div className="text-[9px] font-bold uppercase tracking-wider text-emerald-700">Peak rank</div>
                    <div className="text-xl font-bold font-mono tabular-nums text-emerald-700 mt-0.5">
                      #{t.peak_rank}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Rank history</div>
                  <div className="flex flex-wrap gap-1.5">
                    {ranksByDay.map(p => (
                      <span key={p.day} className="px-2 py-0.5 rounded-md bg-slate-50 border border-slate-200 text-[10.5px] font-mono tabular-nums text-slate-700">
                        {new Date(p.day).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · <span className="font-bold" style={{ color: colorByAsin[t.asin] }}>#{p.rank}</span>
                      </span>
                    ))}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1.5">
                    First seen {new Date(t.first_seen).toLocaleDateString()} · last seen {new Date(t.last_seen).toLocaleDateString()}
                  </div>
                </div>

                {t.url && (
                  <a
                    href={t.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition-colors"
                    data-testid="dd-rank-race-detail-asin-link"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View on Amazon
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Brand drilldown — opens from the Slope-Dumbbell row click.
          Lists every ASIN in the race that shares the clicked brand,
          each clickable to jump into the per-ASIN detail modal above. */}
      {brandDrill && (() => {
        const matching = allTracks.filter(x => (x.brand || x.asin) === brandDrill);
        if (!matching.length) return null;
        const climbCount  = matching.filter(x => (x.prev_rank_top != null && x.curr_rank_top != null && x.prev_rank_top > x.curr_rank_top)).length;
        const fallCount   = matching.filter(x => (x.prev_rank_top != null && x.curr_rank_top != null && x.prev_rank_top < x.curr_rank_top)).length;
        const newCount    = matching.filter(x => x.is_new).length;
        const droppedCount = matching.filter(x => x.is_dropped).length;
        return (
          <div
            className="fixed inset-0 z-[65] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setBrandDrill(null)}
            data-testid="dd-rank-race-brand-drill"
          >
            <div
              className="bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3 bg-indigo-50/60">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-indigo-600">Brand drilldown</div>
                  <div className="text-lg font-bold text-slate-900 truncate" title={brandDrill}>{brandDrill}</div>
                  <div className="text-[11px] text-slate-500 font-mono tabular-nums mt-0.5">
                    {matching.length} ASIN{matching.length === 1 ? "" : "s"} in rank race
                    {climbCount    > 0 && <span className="text-emerald-600"> · ▲ {climbCount}</span>}
                    {fallCount     > 0 && <span className="text-rose-600"> · ▼ {fallCount}</span>}
                    {newCount      > 0 && <span className="text-emerald-600"> · ✦ {newCount} NEW</span>}
                    {droppedCount  > 0 && <span className="text-rose-600"> · ✕ {droppedCount} dropped</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setBrandDrill(null)}
                  className="shrink-0 w-8 h-8 rounded-full bg-white shadow-sm hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors"
                  data-testid="dd-rank-race-brand-drill-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
                {matching
                  .slice()
                  .sort((a, b) => (a.curr_rank_top ?? 999) - (b.curr_rank_top ?? 999))
                  .map(x => {
                    const delta = (x.prev_rank_top != null && x.curr_rank_top != null) ? (x.prev_rank_top - x.curr_rank_top) : null;
                    let pill = { text: "held", cls: "bg-slate-100 text-slate-500 border-slate-200" };
                    if (x.is_new)         pill = { text: `NEW · from #${x.prev_rank_full ?? "—"}`, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
                    else if (x.is_dropped) pill = { text: `DROPPED · to #${x.curr_rank_full ?? "—"}`, cls: "bg-rose-50 text-rose-700 border-rose-200" };
                    else if (delta != null && delta > 0) pill = { text: `▲ ${delta}`, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
                    else if (delta != null && delta < 0) pill = { text: `▼ ${-delta}`, cls: "bg-rose-50 text-rose-700 border-rose-200" };
                    return (
                      <button
                        key={x.asin}
                        type="button"
                        onClick={() => { setBrandDrill(null); setDetailAsin(x.asin); }}
                        className="w-full group flex items-center gap-3 p-2.5 rounded-lg ring-1 ring-transparent hover:ring-indigo-200 hover:bg-slate-50 transition-all text-left"
                        data-testid={`dd-rank-race-brand-drill-row-${x.asin}`}
                      >
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-indigo-500 text-white text-[10.5px] font-bold tabular-nums shrink-0">
                          #{x.curr_rank_top ?? x.curr_rank_full ?? "—"}
                        </span>
                        {x.image && <img src={x.image} alt="" className="w-10 h-10 rounded object-cover bg-slate-100 shrink-0 ring-1 ring-slate-200" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] font-semibold text-slate-800 truncate" title={x.title}>{x.title || x.asin}</div>
                          <div className="text-[10px] text-slate-500 font-mono tabular-nums mt-0.5">
                            {x.asin}
                            {x.prev_rank_top != null && <span> · was #{x.prev_rank_top}</span>}
                            {x.prev_rank_top == null && x.prev_rank_full != null && <span> · was #{x.prev_rank_full}</span>}
                          </div>
                        </div>
                        <span className={`inline-flex items-center h-[22px] px-2 rounded-full text-[10px] font-bold border ${pill.cls} tabular-nums shrink-0`}>
                          {pill.text}
                        </span>
                        <ExternalLink className="w-3.5 h-3.5 text-slate-300 group-hover:text-indigo-500 shrink-0" />
                      </button>
                    );
                  })}
              </div>
              <div className="px-5 py-2.5 border-t border-slate-100 bg-slate-50 text-[10px] font-mono tabular-nums text-slate-500 flex items-center justify-between">
                <span>{matching.length} ASIN{matching.length === 1 ? "" : "s"}</span>
                <span>Click any row for per-ASIN rank history</span>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};


// ─── Section-jump navigation rail ───────────────────────────────────
// Prominent vertical rail on the right edge of the deep dive with FULLY
// LABELED pills (no hover-only tooltips). Uses IntersectionObserver to
// track the currently-visible section — active pill lights up in indigo,
// inactive pills stay glassy white. A thin vertical progress bar on the
// left of the rail tracks scroll depth so the user always knows where
// they are in the story.
const DD_SECTIONS = [
  { id: "dd-section-1", num: "01", label: "Market Structure" },
  { id: "dd-section-2", num: "02", label: "Pricing" },
  { id: "dd-section-3", num: "03", label: "Demand" },
  { id: "dd-section-4", num: "04", label: "Movement" },
  { id: "dd-section-5", num: "05", label: "Explorer" },
];

const SectionNav = () => {
  const [activeId, setActiveId] = useState(DD_SECTIONS[0].id);
  const [visible, setVisible] = useState(false);

  // Show the rail only after the reader has scrolled INTO the sections
  // area — i.e. past the hero. We use the hero-to-section gap: once the
  // FIRST section's TOP has crossed the viewport-top (or the reader has
  // scrolled past ~40% of the viewport height) the nav stays visible.
  useEffect(() => {
    const handler = () => {
      const first = document.getElementById(DD_SECTIONS[0].id);
      const threshold = Math.max(200, Math.round(window.innerHeight * 0.4));
      if (!first) { setVisible(window.scrollY > threshold); return; }
      const firstTop = first.getBoundingClientRect().top + window.scrollY;
      // Show once the reader has approached OR passed the first section.
      // 120 px "sneak" so the nav appears just before section-1 becomes
      // the reading focus (and stays visible even while section-1 is on
      // screen, unlike the earlier "top < 80" trigger which vanished when
      // section-1 was pinned at the top after a click-jump).
      setVisible(window.scrollY + 120 >= firstTop);
    };
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, []);

  // Track which section is currently front-and-center.
  useEffect(() => {
    const elements = DD_SECTIONS
      .map(s => document.getElementById(s.id))
      .filter(Boolean);
    if (!elements.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        const inView = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (inView.length > 0) setActiveId(inView[0].target.id);
      },
      { rootMargin: "-20% 0px -65% 0px", threshold: 0 },
    );
    elements.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  const jump = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const activeIndex = Math.max(0, DD_SECTIONS.findIndex(s => s.id === activeId));
  const progressPct = ((activeIndex + 1) / DD_SECTIONS.length) * 100;

  return (
    <nav
      className={`hidden md:flex fixed top-[72px] left-1/2 -translate-x-1/2 z-[60] transition-all duration-300
        ${visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-4 pointer-events-none"}`}
      aria-label="Deep-dive sections"
      data-testid="dd-section-nav"
      data-visible={visible ? "true" : "false"}
    >
      <div className="relative flex items-center gap-1 bg-white/60 backdrop-blur-xl border border-slate-200/50 rounded-xl px-2 py-1.5 shadow-lg ring-1 ring-black/5">
        {DD_SECTIONS.map((s) => {
          const active = activeId === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => jump(s.id)}
              className={`relative inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold tracking-tight transition-all
                ${active
                  ? "bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-md"
                  : "text-slate-600 hover:bg-white/60 hover:text-indigo-700"}`}
              aria-label={`Jump to ${s.label}`}
              aria-current={active ? "true" : undefined}
              data-testid={`dd-section-nav-${s.id}`}
            >
              <span
                className={`inline-flex items-center justify-center min-w-[20px] h-4 px-1 rounded text-[9px] font-mono font-bold tabular-nums
                  ${active ? "bg-white/25 text-white" : "bg-slate-200/70 text-slate-500"}`}
              >
                {s.num}
              </span>
              <span className="text-[12px] leading-none">{s.label}</span>
            </button>
          );
        })}
      </div>
      {/* Progress bar under the pills */}
      <div className="absolute -bottom-1 left-4 right-4 h-0.5 bg-slate-200/40 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-500 rounded-full opacity-80"
          style={{ width: `${progressPct}%` }}
        />
      </div>
    </nav>
  );
};


// ─── Main component ────────────────────────────────────────────────
const CategoryDeepDive = ({ category, brands = [], marketplace, days = 30, onClose, inline = false, onAliasSaved, platform = "amazon-bestseller", displayName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hhiOpen, setHhiOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const printableRef = useRef(null);

  const handleDownloadPdf = async () => {
    if (!printableRef.current || downloading) return;
    setDownloading(true);

    // To bypass html2canvas's viewport-clipping (the printable node lives
    // inside a `overflow-y-auto` scroll box and an `overflow-hidden` modal,
    // both of which clip the rendered output), we mount a DEEP CLONE of
    // the printable subtree into an off-screen container that has no
    // overflow constraint. html2canvas then sees the full content.
    const original = printableRef.current;
    const stage = document.createElement('div');
    stage.style.position = 'fixed';
    stage.style.left = '-99999px';
    stage.style.top = '0';
    stage.style.width = original.scrollWidth + 'px';
    stage.style.height = 'auto';
    stage.style.background = '#ffffff';
    stage.style.zIndex = '-1';
    stage.setAttribute('aria-hidden', 'true');
    const clone = original.cloneNode(true);
    // Wipe overflow constraints on the clone and every descendant.
    clone.style.height = 'auto';
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';
    clone.style.overflowY = 'visible';
    clone.style.width = original.scrollWidth + 'px';
    stage.appendChild(clone);
    // Force EVERY `<Reveal>` (and any other opacity-0 scroll-reveal wrapper)
    // to its visible state. Cloned IntersectionObservers never fire on this
    // off-screen stage, so any descendant with `opacity-0` from a Reveal
    // would render blank in the PDF.
    //
    // IMPORTANT: scope this very narrowly — earlier passes did
    // `transform: none !important` on ALL descendants which broke layout
    // elsewhere (e.g. `-translate-x-1/2` centering helpers), causing
    // absolutely-positioned content to overlap. We only undo the
    // Reveal-specific translate-y AND opacity-0; everything else stays
    // exactly as the live DOM rendered it.
    const revealStyle = document.createElement('style');
    revealStyle.textContent = `
      [data-pdf-stage] { animation: none !important; transition: none !important; }
      [data-pdf-stage] .opacity-0 { opacity: 1 !important; }
      [data-pdf-stage] .translate-y-6 { --tw-translate-y: 0px !important; transform: translate(var(--tw-translate-x, 0), 0) !important; }
      [data-pdf-stage] .translate-y-4 { --tw-translate-y: 0px !important; transform: translate(var(--tw-translate-x, 0), 0) !important; }
      [data-pdf-stage] .translate-y-2 { --tw-translate-y: 0px !important; transform: translate(var(--tw-translate-x, 0), 0) !important; }
      [data-pdf-stage] [class*="opacity-0"] { opacity: 1 !important; }
    `;
    document.head.appendChild(revealStyle);
    stage.setAttribute('data-pdf-stage', '1');
    document.body.appendChild(stage);

    // Wait one frame for layout reflow on the freshly mounted clone.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    try {
      const fullHeight = Math.max(clone.scrollHeight, clone.getBoundingClientRect().height);
      // Canvas height has a hard browser cap (~16384px on most engines).
      // Pick scale based on content height so we always stay below.
      const targetCanvasHeight = 12000;
      const scale = Math.max(1, Math.min(2, targetCanvasHeight / Math.max(fullHeight, 1)));

      // Wrap html2canvas in a timeout race — it can hang on unloadable
      // images or oversize canvases, leaving the button stuck.
      const renderPromise = html2canvas(clone, {
        backgroundColor: "#ffffff",
        scale,
        useCORS: true,
        allowTaint: false,
        logging: false,
        imageTimeout: 4000,
        width: clone.scrollWidth,
        height: fullHeight,
        windowWidth: clone.scrollWidth,
        windowHeight: fullHeight,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc) => {
          // Replace every <img> with a same-sized neutral placeholder so the
          // canvas never gets CORS-tainted by Amazon CDN images.
          clonedDoc.querySelectorAll('img').forEach((img) => {
            const rect = img.getBoundingClientRect();
            const ph = clonedDoc.createElement('div');
            ph.style.width = (rect.width || img.width || 36) + 'px';
            ph.style.height = (rect.height || img.height || 36) + 'px';
            ph.style.backgroundColor = '#f1f5f9';
            ph.style.borderRadius = '4px';
            ph.style.display = 'inline-block';
            ph.style.flexShrink = '0';
            ph.className = img.className;
            img.replaceWith(ph);
          });
          // Unfurl the printable container — the live DOM has it as a
          // scroll-clipped (`overflow-y: auto`, `flex-1`) box, so its
          // rendered viewport is only the visible area. We need html2canvas
          // to see the FULL content height. Also unfurl every ancestor that
          // might clip the scroll. (Match by data-testid to find the right
          // node in the cloned tree.)
          const printable = clonedDoc.querySelector('[data-testid="category-deepdive-modal"]');
          if (printable) {
            printable.style.position = 'static';
            printable.style.height = 'auto';
            printable.style.minHeight = 'auto';
            printable.style.maxHeight = 'none';
            printable.style.overflow = 'visible';
            // The first descendant with overflow-y-auto is our scroll root.
            printable.querySelectorAll('*').forEach((el) => {
              const cs = el.style;
              if (cs && (cs.overflowY === 'auto' || cs.overflow === 'auto' || cs.overflowY === 'scroll' || cs.overflow === 'scroll')) {
                cs.overflow = 'visible';
                cs.overflowY = 'visible';
              }
            });
            // Tailwind utility classes apply via CSS rules, not inline,
            // so we patch them by injecting a stylesheet override.
            const overflowReset = clonedDoc.createElement('style');
            overflowReset.textContent = `
              .overflow-y-auto, .overflow-y-scroll, .overflow-auto, .overflow-scroll,
              .overflow-hidden, .overflow-y-hidden { overflow: visible !important; overflow-y: visible !important; }
              .max-h-\\[100vh\\], .max-h-\\[96vh\\], .h-\\[100vh\\], .h-\\[96vh\\],
              .h-screen, .max-h-screen { height: auto !important; max-height: none !important; min-height: 0 !important; }
              .flex-1 { flex: none !important; }
              .fixed { position: static !important; }
            `;
            (clonedDoc.head || clonedDoc.documentElement).appendChild(overflowReset);
          }
          // Nuke ALL background images globally. html2canvas raster-pattern
          // pipeline can produce 0×0 canvases for tiny radial-gradient tiles
          // (and some Tailwind `bg-gradient-to-*` rules), which then crash
          // `createPattern` with "image argument has width or height of 0".
          const style = clonedDoc.createElement('style');
          style.textContent = `* {
            background-image: none !important;
            background-attachment: scroll !important;
            background-repeat: no-repeat !important;
            background-size: auto !important;
          }`;
          (clonedDoc.head || clonedDoc.documentElement).appendChild(style);
          // Also strip any empty / zero-size <canvas> elements.
          clonedDoc.querySelectorAll('canvas').forEach((c) => {
            if (!c.width || !c.height) c.remove();
          });
        },
      });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Render took too long (>45s)')), 45000)
      );
      const canvas = await Promise.race([renderPromise, timeoutPromise]);

      const imgWidth = 595;   // A4 width in pt at 72dpi
      const pageHeight = 842; // A4 height in pt
      const pdf = new jsPDF("p", "pt", "a4");
      // Slice the rendered canvas into per-page chunks. Earlier approach
      // (re-using one tall image with negative offsets) renders blank pages
      // on jsPDF 4.x when content extends beyond the first page.
      const scaleRatio = imgWidth / canvas.width;
      const pageCanvasHeight = Math.floor(pageHeight / scaleRatio);
      const pages = Math.ceil(canvas.height / pageCanvasHeight);
      for (let p = 0; p < pages; p++) {
        const sy = p * pageCanvasHeight;
        const sh = Math.min(pageCanvasHeight, canvas.height - sy);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sh;
        const sctx = sliceCanvas.getContext("2d");
        sctx.fillStyle = "#ffffff";
        sctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        sctx.drawImage(canvas, 0, sy, canvas.width, sh, 0, 0, sliceCanvas.width, sh);
        const sliceDataUrl = sliceCanvas.toDataURL("image/jpeg", 0.88);
        if (p > 0) pdf.addPage();
        const renderH = sh * scaleRatio;
        pdf.addImage(sliceDataUrl, "JPEG", 0, 0, imgWidth, renderH);
      }
      const safeName = (category || "category").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      const ts = new Date().toISOString().slice(0, 10);
      pdf.save(`horizon-deepdive-${safeName}-${ts}.pdf`);
    } catch (e) {
      console.error("PDF export failed", e);
      // eslint-disable-next-line no-alert
      alert(`PDF export failed: ${e?.message || 'unknown error'} — please try again.`);
    } finally {
      // Clean up the off-screen clone stage and its associated style tag.
      if (stage && stage.parentNode) stage.parentNode.removeChild(stage);
      if (revealStyle && revealStyle.parentNode) revealStyle.parentNode.removeChild(revealStyle);
      setDownloading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const params = { category, days };
        if (brands && brands.length > 0) params.brands = brands.join(",");
        if (marketplace) params.marketplace = marketplace;
        if (platform && platform !== "amazon-bestseller") params.platform = platform;
        const r = await axios.get(`${API}/amazon/bestseller/category-deepdive`, { params });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load deep dive");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, days, brands.join(","), marketplace, platform]);

  // ── Brand share basis — prefer units sold (Apify's bought-in-past-month)
  // when backend has enough demand data; fall back to listing count.
  const shareBasis = data?.share_basis || "listings";
  const brandShare = useMemo(() => {
    if (shareBasis === "units" && data?.brand_share_units?.length) {
      return data.brand_share_units;
    }
    return data?.brand_share || [];
  }, [data, shareBasis]);
  const hhi = data?.hhi || 0;
  const hhiInfo = hhiLabel(hhi);
  // ── Top-3 brand share (CR3) ─ replaces HHI for general users ────────
  const top3Pct = useMemo(() => {
    if (!brandShare.length) return 0;
    return Number(
      brandShare.slice(0, 3).reduce((s, b) => s + (b.share_pct || 0), 0).toFixed(1)
    );
  }, [brandShare]);
  const top3Info = top3Label(top3Pct);
  const top3Brands = brandShare.slice(0, 3) || [];

  // ── Marketplace (Amazon country) — drives currency + flag rendering ──
  const marketplaceCode = (data?.marketplace || "IN").toUpperCase();
  const mp = getMarketplace(marketplaceCode);
  const fmtP = (n) => fmtMoney(n, marketplaceCode);
  // ── Brand-filter state coming from the URL params (Market Research filter).
  // `appliedBrands` is what the backend actually matched (with proper casing).
  const appliedBrands = data?.applied_brands || [];
  const brandFiltered = appliedBrands.length > 0;
  const isSingleBrand = appliedBrands.length === 1;
  const categoryTotal = data?.category_total_products ?? data?.products ?? 0;

  // ── Drilldown modal — opens on brand-tile click or price-quadrant zone click.
  //    Shows the list of contributing products in that scope.
  const [drilldown, setDrilldown] = useState(null);   // { title, subtitle, products, accent }
  const [aliasModal, setAliasModal] = useState(null); // { attribute, rawValue }

  // ─── Section visibility (Control Panel) ────────────────────────────
  // Every top-level Deep Dive section AND every optional sub-widget is
  // toggleable from a single panel. Preferences persist in localStorage
  // (not category-scoped) so the same layout applies to every category
  // the user opens. Top-level sections default to VISIBLE; optional
  // sub-widgets default to HIDDEN (unchanged from before).
  const DEFAULT_WIDGET_VIS = {
    // ── Top-level sections (visible by default) ──
    market_structure:  true,
    rank_race:         true,   // bump chart of top-10 movement (lives inside Market Structure)
    pricing_landscape: true,   // aka "02 · Pricing Landscape" whole block
    demand_signals:    true,
    movement:          true,   // "04 · Movement" — new entrants list
    product_explorer:  true,   // "Every ASIN in this category" grid
    // ── Optional sub-widgets (hidden by default) ──
    price_positioning:         false,
    pack_sizes:                false,
    audience_signal:           false,
    price_rating_reviews:      false,
  };
  const [widgetVis, setWidgetVis] = useState(() => {
    try {
      const raw = localStorage.getItem("dd-widget-vis");
      if (raw) return { ...DEFAULT_WIDGET_VIS, ...JSON.parse(raw) };
    } catch { /* localStorage unavailable — ignore */ }
    return DEFAULT_WIDGET_VIS;
  });
  const [controlPanelOpen, setControlPanelOpen] = useState(false);
  const toggleWidget = (key) => {
    setWidgetVis(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem("dd-widget-vis", JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };
  // Structured for the panel: grouped labels + optional description. The
  // "sections" group toggles WHOLE top-level blocks; the "optional_widgets"
  // group toggles inner widgets inside Pricing Landscape.
  const WIDGET_GROUPS = [
    {
      title: "Sections",
      desc: "Show or hide entire chapters of the deep dive.",
      items: [
        { key: "market_structure",  label: "01 · Market Structure" },
        { key: "rank_race",         label: "     Rank Race chart (top-10 movement)" },
        { key: "pricing_landscape", label: "02 · Pricing Landscape" },
        { key: "demand_signals",    label: "03 · Demand Signals" },
        { key: "movement",          label: "04 · Movement (new entrants list)" },
        { key: "product_explorer",  label: "Every ASIN in this category" },
      ],
    },
    {
      title: "Optional widgets · Pricing Landscape",
      desc: "These sub-widgets inside Pricing Landscape are hidden by default. Enable the ones you want to see.",
      items: [
        { key: "price_positioning",         label: "Price Positioning · 2×2" },
        { key: "pack_sizes",                label: "Pack sizes" },
        { key: "audience_signal",           label: "Audience Signal (Diet · Specialty)" },
        { key: "price_rating_reviews",      label: "Price × Rating × Reviews" },
      ],
    },
  ];
  // ─── Staged (unsaved) changes ─────────────────────────────────────
  // Every alias edit or manual +Add now queues into `pendingChanges` and
  // is applied optimistically to `data.explorer` in-place. A floating
  // "Save changes" pill commits the whole batch in ONE round-trip
  // (bulk alias upsert + parallel manual-attribute POSTs) followed by a
  // single refetch. This eliminates the "reload every edit" thrashing.
  //
  // Shape:
  //   { id, kind: "alias"|"manual", attribute, ...alias fields..., ...manual fields... }
  //   alias:  { raw_value, display_value }
  //   manual: { asin, value }
  const [pendingChanges, setPendingChanges] = useState([]);
  const [savingBatch, setSavingBatch] = useState(false);
  // Expand/collapse the pending-edits tray — defaults to COLLAPSED
  // so the pill stays compact; auto-expands any time a save errors so
  // the user can retry the failed items without hunting.
  const [pendingExpanded, setPendingExpanded] = useState(false);

  // Apply staged changes optimistically to the currently-loaded explorer
  // rows. Called after every stage() to give instant UI feedback.
  const applyOptimistic = (changes) => {
    setData(prev => {
      if (!prev || !prev.explorer) return prev;
      const explorer = prev.explorer.map(p => ({ ...p }));
      for (const c of changes) {
        if (c.kind === "alias") {
          const rawKey = String(c.raw_value || "").toLowerCase();
          for (const p of explorer) {
            const rowRaw = String(p[`${c.attribute}_raw`] || "").toLowerCase();
            if (rowRaw && rowRaw === rawKey) {
              p[c.attribute] = c.display_value;
            }
          }
        } else if (c.kind === "manual") {
          const target = explorer.find(p => p.asin === c.asin);
          if (target) {
            target[c.attribute] = c.value;
            target[`${c.attribute}_raw`] = c.value;
          }
        }
      }
      return { ...prev, explorer };
    });
  };

  const stageAliasChange = (change) => {
    // change: { attribute, raw_value, display_value, category }
    const withId = { id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, kind: "alias", ...change };
    setPendingChanges(prev => {
      // De-dupe: same (attribute, raw_value, category) → replace previous
      // entry. Category MUST be part of the key — otherwise staging a
      // "Global" change silently drops an earlier "Only in Creatine"
      // change for the same raw (or vice-versa), and the user loses one
      // of the two mappings they intended to make.
      const normCat = (change.category || "").trim().toLowerCase();
      const filtered = prev.filter(c => {
        if (c.kind !== "alias") return true;
        if (c.attribute !== change.attribute) return true;
        if ((c.raw_value || "").toLowerCase() !== (change.raw_value || "").toLowerCase()) return true;
        const cCat = (c.category || "").trim().toLowerCase();
        return cCat !== normCat;
      });
      const next = [...filtered, withId];
      applyOptimistic([withId]);
      return next;
    });
  };

  const stageManualChange = (change) => {
    // change: { asin, attribute, value }
    const withId = { id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, kind: "manual", ...change };
    setPendingChanges(prev => {
      // De-dupe: same asin + attribute → replace previous entry
      const filtered = prev.filter(c =>
        !(c.kind === "manual" && c.asin === change.asin && c.attribute === change.attribute)
      );
      const next = [...filtered, withId];
      applyOptimistic([withId]);
      return next;
    });
  };

  // Commit every pending change in ONE batch: bulk aliases + parallel
  // manual-attribute POSTs, then a single refetch. If some items fail
  // we keep them in the queue (they still show in the floating pill) so
  // the user can retry or discard them explicitly.
  const saveAllPending = async () => {
    if (!pendingChanges.length || savingBatch) return;
    setSavingBatch(true);
    // Snapshot the queue at the moment the user clicked Save so any edits
    // the user stages DURING the network round-trip are preserved (previously
    // we cleared the whole list unconditionally, dropping in-flight changes).
    const snapshot = [...pendingChanges];
    const t = toast.loading(`Saving ${snapshot.length} change${snapshot.length === 1 ? '' : 's'}…`);
    const aliasesWithId = snapshot.filter(c => c.kind === "alias");
    const aliases = aliasesWithId.map(c => ({
      attribute: c.attribute,
      raw_value: c.raw_value,
      display_value: c.display_value,
      category: c.category || null,
    }));
    const manuals = snapshot.filter(c => c.kind === "manual");
    const failedIds = new Set();   // IDs that errored — keep these in the queue
    const succeededIds = new Set(); // IDs that saved — remove these on exit
    let aliasOk = 0;
    let manualOk = 0;
    let errors = 0;
    try {
      if (aliases.length) {
        const r = await axios.post(`${API}/attribute-aliases/bulk`, { aliases });
        aliasOk = (r.data?.created || 0) + (r.data?.updated || 0);
        const errList = r.data?.errors || [];
        errors += errList.length;
        // Backend returns `{index, error}` per failed row — map back to
        // our staged IDs by position in `aliasesWithId`.
        const errIdx = new Set(errList.map(e => e.index));
        aliasesWithId.forEach((c, i) => {
          if (errIdx.has(i)) failedIds.add(c.id);
          else succeededIds.add(c.id);
        });
      }
      if (manuals.length) {
        const settled = await Promise.allSettled(
          manuals.map(m => axios.post(`${API}/amazon/bestseller/analytics/manual-attribute`, {
            asin: m.asin, attribute: m.attribute, value: m.value,
          }))
        );
        settled.forEach((s, i) => {
          if (s.status === "fulfilled") { manualOk += 1; succeededIds.add(manuals[i].id); }
          else { errors += 1; failedIds.add(manuals[i].id); }
        });
      }
    } catch {
      // Network-level failure — mark EVERY attempted item as failed so
      // the user can retry the batch after fixing connectivity.
      snapshot.forEach(c => failedIds.add(c.id));
      errors += 1;
    }
    toast.dismiss(t);
    if (errors === 0) {
      toast.success(`Saved · ${aliasOk} alias${aliasOk === 1 ? '' : 'es'} · ${manualOk} manual value${manualOk === 1 ? '' : 's'}.`);
    } else {
      toast.error(`${errors} change(s) failed — kept in queue to retry. Refetching server state.`);
      // Auto-open the tray so the user sees exactly which rows need attention.
      setPendingExpanded(true);
    }
    // Remove only the items that actually saved; leave failed + in-flight.
    setPendingChanges(prev => prev.filter(c => !succeededIds.has(c.id)));
    setSavingBatch(false);
    // Final source of truth from server. Chain-aliases (e.g. user pencils a
    // tile that's already aliased once; the modal captures the DISPLAY value,
    // not the underlying raw) are handled server-side by the two-pass canon
    // + alias resolver for flavour. For brand/other attrs we intentionally
    // keep single-step alias so users can undo a chain by re-editing the raw.
    await refetchAfterAlias();
  };

  const discardPending = async () => {
    if (!pendingChanges.length) return;
    if (!window.confirm(`Discard ${pendingChanges.length} unsaved change${pendingChanges.length === 1 ? '' : 's'}?`)) return;
    setPendingChanges([]);
    // Roll back optimistic state to server truth
    await refetchAfterAlias();
  };

  const [backfilling, setBackfilling] = useState(false);
  // { total, enriched, rowsUpdated, iterations, remaining, phase, stalled }
  const [backfillProgress, setBackfillProgress] = useState(null);
  const backfillCancelRef = useRef(false);

  // Auto-looping PDP re-enrichment scoped to the currently-open category.
  // Runs a dry-run to size the job, then loops the actual backfill (50 ASINs
  // per iteration) until every recoverable row is filled OR we detect that
  // the remaining ASINs are bot-blocked (a full iteration recovers 0). A
  // live progress bar shows enriched / total_missing while the loop runs.
  const BACKFILL_BATCH = 50;
  const BACKFILL_MAX_ITERS = 40;         // hard safety cap → 2000 ASINs max
  const BACKFILL_STALL_LIMIT = 2;        // stop after N consecutive zero-enrichment iterations

  const runBackfill = async () => {
    if (backfilling || !category) return;
    setBackfilling(true);
    backfillCancelRef.current = false;
    setBackfillProgress({ total: 0, enriched: 0, rowsUpdated: 0, iterations: 0, remaining: 0, phase: "sizing", stalled: 0 });
    try {
      // Pass 1 — dry run to size the job.
      const dry = await axios.post(`${API}/amazon/bestseller/analytics/backfill-pdp`, null, {
        params: { category, dry_run: true, limit: BACKFILL_BATCH },
      });
      const total = dry.data?.total_missing || 0;
      if (!total) {
        toast.success(`${category}: nothing to backfill — every ASIN has flavour, form and pack size.`);
        setBackfillProgress(null);
        setBackfilling(false);
        return;
      }
      if (!window.confirm(`${total} ASIN(s) in "${category}" are missing mandatory attributes. Auto-loop backfill will run until done (up to ${BACKFILL_MAX_ITERS * BACKFILL_BATCH}). Continue?`)) {
        setBackfillProgress(null);
        setBackfilling(false);
        return;
      }

      // Pass 2 — loop 50 at a time until remaining hits 0 (or we stall).
      let enrichedTotal = 0;
      let rowsUpdatedTotal = 0;
      let remaining = total;
      let iters = 0;
      let stalled = 0;

      setBackfillProgress({ total, enriched: 0, rowsUpdated: 0, iterations: 0, remaining, phase: "running", stalled: 0 });

      while (remaining > 0 && iters < BACKFILL_MAX_ITERS) {
        if (backfillCancelRef.current) break;
        iters += 1;
        try {
          const res = await axios.post(`${API}/amazon/bestseller/analytics/backfill-pdp`, null, {
            params: { category, dry_run: false, limit: BACKFILL_BATCH },
          });
          const d = res.data || {};
          const gotEnriched = Number(d.enriched || 0);
          enrichedTotal += gotEnriched;
          rowsUpdatedTotal += Number(d.rows_updated || 0);
          remaining = Number(d.remaining_after_run ?? Math.max(0, total - enrichedTotal));

          // Bot-blocked / unrecoverable ASINs will surface as 0-enrichment
          // iterations. After a couple of those in a row, stop — otherwise
          // we spin forever against the same bad ASINs.
          if (gotEnriched === 0) {
            stalled += 1;
          } else {
            stalled = 0;
          }
          setBackfillProgress({ total, enriched: enrichedTotal, rowsUpdated: rowsUpdatedTotal, iterations: iters, remaining, phase: "running", stalled });
          if (stalled >= BACKFILL_STALL_LIMIT) {
            break;
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || `Iteration ${iters} failed — stopping.`);
          break;
        }
      }

      setBackfillProgress(p => ({ ...(p || {}), phase: "done" }));
      if (stalled >= BACKFILL_STALL_LIMIT && remaining > 0) {
        toast(`Backfill stopped: ${remaining} ASIN(s) appear bot-blocked. Enriched ${enrichedTotal} · updated ${rowsUpdatedTotal} rows.`, { icon: "⚠️" });
      } else if (backfillCancelRef.current) {
        toast(`Backfill cancelled. Enriched ${enrichedTotal} · updated ${rowsUpdatedTotal} rows.`, { icon: "🛑" });
      } else {
        toast.success(`Backfill complete: enriched ${enrichedTotal}/${total} · updated ${rowsUpdatedTotal} rows.`);
      }
      // Deep-dive data reflects the new attrs — refetch.
      await refetchAfterAlias();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    }
    setBackfilling(false);
    // Keep the progress card visible for a moment so the user sees the
    // final counts, then clear it.
    setTimeout(() => setBackfillProgress(null), 4000);
  };

  const cancelBackfill = () => {
    backfillCancelRef.current = true;
  };

  const refetchAfterAlias = async () => {
    // Reload the deep dive after the user saves a new alias so brand/flavour
    // aggregations reflect the new display value immediately. Also notify
    // the parent (Market Research Analytics) so its category/brand/flavour
    // aggregates pick up the new alias too — keeps everything in sync.
    try {
      // `_t` cache-buster ensures we never hit a stale HTTP-cached response
      // (some ingress/CDN paths may cache the GET even without explicit
      // Cache-Control), which used to make alias saves look like no-ops.
      const params = { category, days, _t: Date.now() };
      if (brands && brands.length > 0) params.brands = brands.join(",");
      if (marketplace) params.marketplace = marketplace;
      if (platform && platform !== "amazon-bestseller") params.platform = platform;
      const r = await axios.get(`${API}/amazon/bestseller/category-deepdive`, { params });
      setData(r.data);
    } catch {/* swallow — user will see toast from POST */ }
    if (onAliasSaved) {
      try { await onAliasSaved(); } catch {/* parent refresh is best-effort */}
    }
  };

  const openBrandDrilldown = (b) => {
    const want = (b.brand || "").toLowerCase();
    const prods = (data?.explorer || []).filter(p => (p.brand || "").toLowerCase() === want);
    setDrilldown({
      title: b.brand,
      subtitle: `${prods.length} product${prods.length === 1 ? '' : 's'} · ${b.share_pct}% of category shelf`,
      products: prods,
      accent: "indigo",
    });
  };

  const openZoneDrilldown = (z) => {
    setDrilldown({
      title: z.label,
      subtitle: `${z.totalInZone} brand${z.totalInZone === 1 ? '' : 's'} · ${z.range} · ${z.products.length} product${z.products.length === 1 ? '' : 's'}`,
      products: z.products || [],
      accent: z.key === "premium" ? "amber" : z.key === "budget" ? "emerald" : z.key === "mid_value" ? "rose" : "slate",
    });
  };

  // Pack-size card drilldown — clicking a card opens the same modal used for
  // brand / zone drilldowns and lists every product whose (alias-resolved)
  // pack_size matches the clicked label.
  const openPackDrilldown = (label) => {
    const prods = (data?.explorer || []).filter(p => (p.pack_size || "") === label);
    setDrilldown({
      title: label,
      subtitle: `${prods.length} product${prods.length === 1 ? '' : 's'} · ${label} pack size`,
      products: prods,
      accent: "emerald",
    });
  };

  // Price-band drilldown — bar in the "02 · Pricing Landscape" histogram.
  // Band shape (from backend `price_bands`): { label, lo, hi, count }. The
  // last band is inclusive of its upper bound; every other band is [lo, hi).
  // We identify "last" by matching the band to the max `hi` in the series.
  const openPriceBandDrilldown = (band) => {
    if (!band || band.lo == null || band.hi == null) return;
    const bands = data?.price_bands || [];
    const maxHi = bands.reduce((m, b) => Math.max(m, b.hi ?? 0), 0);
    const isLast = band.hi === maxHi;
    const prods = (data?.explorer || []).filter(p => {
      const price = typeof p.price === "number" ? p.price : parseFloat(p.price);
      if (!price && price !== 0) return false;
      if (!isFinite(price)) return false;
      return isLast ? (price >= band.lo && price <= band.hi) : (price >= band.lo && price < band.hi);
    });
    setDrilldown({
      title: band.label,
      subtitle: `${prods.length} listing${prods.length === 1 ? '' : 's'} in this price band`,
      products: prods,
      accent: "emerald",
    });
  };

  // Brand × Flavour heatmap-cell drilldown — filters explorer by both
  // dimensions so the analyst can jump from the cell to the actual SKUs.
  const openBrandFlavourDrilldown = (brand, flavour) => {
    const b = String(brand || "").toLowerCase();
    const f = String(flavour || "").toLowerCase();
    const prods = (data?.explorer || []).filter(p =>
      String(p.brand || "").toLowerCase() === b &&
      String(p.flavour || "").toLowerCase() === f
    );
    setDrilldown({
      title: `${brand} × ${flavour}`,
      subtitle: `${prods.length} product${prods.length === 1 ? '' : 's'} · brand × flavour`,
      products: prods,
      accent: "indigo",
    });
  };

  // Generic attribute drilldown — used by Diet Type / Specialty / Flavour /
  // Item Form cards. Filters `data.explorer` by an exact match on the
  // (alias-resolved) attribute value the user clicked, then reuses the same
  // drilldown modal to render the product list with Amazon links.
  const openAttrDrilldown = (attribute, label, accent = "cyan") => {
    const key = String(label || "");
    const prods = (data?.explorer || []).filter(p => String(p[attribute] || "") === key);
    const attrLabel = ({
      brand: "brand",
      flavour: "flavour",
      item_form: "form",
      diet_type: "diet",
      specialty: "specialty",
      pack_size: "pack size",
    })[attribute] || attribute;
    setDrilldown({
      title: label,
      subtitle: `${prods.length} product${prods.length === 1 ? '' : 's'} · ${attrLabel}`,
      products: prods,
      accent,
    });
  };

  return (
    <div
      className={
        inline
          ? "relative w-full"
          : "fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-md flex items-center justify-center p-0 sm:p-4"
      }
      onClick={inline ? undefined : onClose}
      data-testid="category-deepdive-modal"
    >
      <style>{`
        @keyframes pulseGlow { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.8; } }
        @keyframes ddPdfPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.55), 0 8px 24px -6px rgba(15, 23, 42, 0.25); }
          50%      { box-shadow: 0 0 0 10px rgba(99, 102, 241, 0), 0 8px 24px -6px rgba(15, 23, 42, 0.25); }
        }
        .dd-pdf-pulse { animation: ddPdfPulse 2.2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      `}</style>
      <div
        className={
          inline
            ? "bg-white rounded-2xl shadow-sm w-full overflow-hidden flex flex-col border border-slate-200"
            : "bg-white rounded-none sm:rounded-2xl shadow-2xl w-full max-w-[1280px] h-[100vh] sm:h-[96vh] overflow-hidden flex flex-col border border-slate-200"
        }
        onClick={inline ? undefined : (e) => e.stopPropagation()}
      >
        {/* Close button — only when used as a modal */}
        {!inline && (
          <div className="absolute top-3 right-3 sm:top-5 sm:right-5 z-20 flex items-start gap-2">
            {/* Live progress card — visible only while auto-loop is running or */}
            {/* on the final "done" state (briefly). Streams enriched/total per */}
            {/* iteration so the user can watch the drain in real time. */}
            {backfillProgress && (
              <div
                className="bg-white/95 backdrop-blur shadow-lg border border-teal-200 rounded-xl px-3 py-2 w-64 text-[11px]"
                data-testid="dd-backfill-progress"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5 text-teal-700 font-semibold">
                    {backfillProgress.phase === "done"
                      ? <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      : <Loader2 className="w-3 h-3 animate-spin" />}
                    <span>
                      {backfillProgress.phase === "sizing" && "Sizing job…"}
                      {backfillProgress.phase === "running" && `Backfilling · iter ${backfillProgress.iterations}`}
                      {backfillProgress.phase === "done" && "Backfill complete"}
                    </span>
                  </div>
                  {backfilling && backfillProgress.phase === "running" && (
                    <button
                      onClick={cancelBackfill}
                      className="text-slate-400 hover:text-rose-600 text-[10px] font-semibold uppercase tracking-wide"
                      data-testid="dd-backfill-cancel"
                    >
                      Stop
                    </button>
                  )}
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-1.5">
                  <div
                    className="h-full bg-gradient-to-r from-teal-400 to-emerald-500 transition-all duration-500"
                    style={{
                      width: backfillProgress.total
                        ? `${Math.min(100, Math.round((backfillProgress.enriched / backfillProgress.total) * 100))}%`
                        : "0%",
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-slate-500 font-mono tabular-nums">
                  <span data-testid="dd-backfill-progress-count">
                    {backfillProgress.enriched}/{backfillProgress.total || "?"}
                  </span>
                  <span>{backfillProgress.remaining} left</span>
                </div>
                {backfillProgress.stalled > 0 && backfillProgress.phase === "running" && (
                  <div className="mt-1 text-[10px] text-amber-600">
                    {backfillProgress.stalled} zero-enrichment iter — bot-blocked?
                  </div>
                )}
              </div>
            )}
            <button
              onClick={runBackfill}
              disabled={backfilling}
              className="inline-flex items-center gap-1.5 px-2.5 h-9 rounded-full bg-white/90 backdrop-blur shadow-md hover:bg-teal-50 text-teal-700 border border-teal-200 text-[11px] font-semibold transition-colors disabled:opacity-60"
              title="Auto-loop PDP backfill for this category — runs until every ASIN has flavour / form / pack size."
              data-testid="dd-backfill-btn"
            >
              {backfilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Backfill
            </button>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-white/90 backdrop-blur shadow-md hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors"
              data-testid="dd-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {loading ? (
          <div className={inline ? "flex items-center justify-center py-24 text-sm text-slate-400" : "flex-1 flex items-center justify-center text-sm text-slate-400"}>
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className={inline ? "flex items-center justify-center py-24 text-sm text-red-500" : "flex-1 flex items-center justify-center text-sm text-red-500"}>{error}</div>
        ) : !data || data.products === 0 ? (
          <div className={inline ? "flex items-center justify-center py-24 text-sm text-slate-400" : "flex-1 flex items-center justify-center text-sm text-slate-400"}>{data?.message || 'No data for this category yet.'}</div>
        ) : (
          <div ref={printableRef} {...(inline ? { 'data-dd-inline': '1' } : {})} className={inline ? "w-full" : "flex-1 overflow-y-auto scroll-smooth"}>
            {/* ════════════════════════════════════════════════════════════ */}
            {/* HERO — giant numbers, slate background ─────────────────── */}
            {/* ════════════════════════════════════════════════════════════ */}
            <section className="relative bg-gradient-to-b from-slate-50 to-white px-6 sm:px-12 pt-16 sm:pt-20 pb-12 sm:pb-16 overflow-hidden">
              {/* Decorative dot grid */}
              <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
                   style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #0f172a 1px, transparent 0)', backgroundSize: '24px 24px' }} />
              <div className="relative max-w-6xl mx-auto">
                <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="h-px w-12 bg-gradient-to-r from-slate-300 to-slate-600" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">Category · Deep Dive</span>
                  </div>
                  {/* Backfill + PDF cluster */}
                  <div className="flex items-center gap-2">
                    {/* Live backfill progress (inline variant — sits next to */}
                    {/* the buttons and streams counts per iteration). */}
                    {backfillProgress && (
                      <div
                        className="bg-white shadow-md border border-teal-200 rounded-xl px-3 py-2 w-64 text-[11px]"
                        data-testid="dd-backfill-progress-inline"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-1.5 text-teal-700 font-semibold">
                            {backfillProgress.phase === "done"
                              ? <span className="w-2 h-2 rounded-full bg-emerald-500" />
                              : <Loader2 className="w-3 h-3 animate-spin" />}
                            <span>
                              {backfillProgress.phase === "sizing" && "Sizing job…"}
                              {backfillProgress.phase === "running" && `Backfilling · iter ${backfillProgress.iterations}`}
                              {backfillProgress.phase === "done" && "Backfill complete"}
                            </span>
                          </div>
                          {backfilling && backfillProgress.phase === "running" && (
                            <button
                              onClick={cancelBackfill}
                              className="text-slate-400 hover:text-rose-600 text-[10px] font-semibold uppercase tracking-wide"
                              data-testid="dd-backfill-cancel-inline"
                            >
                              Stop
                            </button>
                          )}
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-1.5">
                          <div
                            className="h-full bg-gradient-to-r from-teal-400 to-emerald-500 transition-all duration-500"
                            style={{
                              width: backfillProgress.total
                                ? `${Math.min(100, Math.round((backfillProgress.enriched / backfillProgress.total) * 100))}%`
                                : "0%",
                            }}
                          />
                        </div>
                        <div className="flex items-center justify-between text-slate-500 font-mono tabular-nums">
                          <span data-testid="dd-backfill-progress-count-inline">
                            {backfillProgress.enriched}/{backfillProgress.total || "?"}
                          </span>
                          <span>{backfillProgress.remaining} left</span>
                        </div>
                        {backfillProgress.stalled > 0 && backfillProgress.phase === "running" && (
                          <div className="mt-1 text-[10px] text-amber-600">
                            {backfillProgress.stalled} zero-enrichment iter — bot-blocked?
                          </div>
                        )}
                      </div>
                    )}
                    <button
                      onClick={runBackfill}
                      disabled={backfilling}
                      className="h-10 px-3 rounded-full bg-white hover:bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                      title="Auto-loop PDP backfill — runs until every ASIN has flavour / form / pack size."
                      data-testid="dd-backfill-btn-inline"
                    >
                      {backfilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      {backfilling ? "Backfilling…" : "Backfill"}
                    </button>
                    {/* Control Panel — toggles optional sections on/off */}
                    <div className="relative">
                      <button
                        onClick={() => setControlPanelOpen(v => !v)}
                        className="h-10 px-3 rounded-full bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors"
                        title="Toggle optional Deep Dive sections"
                        data-testid="dd-control-panel-btn"
                      >
                        <Sliders className="w-3.5 h-3.5" />
                        Control Panel
                        {Object.values(widgetVis).filter(Boolean).length > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-indigo-600 text-white text-[9px] font-bold tabular-nums">
                            {Object.values(widgetVis).filter(Boolean).length}
                          </span>
                        )}
                      </button>
                      {controlPanelOpen && (
                        <>
                          {/* Backdrop to close on outside-click */}
                          <div className="fixed inset-0 z-40" onClick={() => setControlPanelOpen(false)} />
                          <div
                            className="absolute right-0 top-12 z-50 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 p-3 max-h-[70vh] overflow-y-auto"
                            data-testid="dd-control-panel-menu"
                          >
                            <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
                              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Deep Dive Sections</span>
                              <button
                                onClick={() => setControlPanelOpen(false)}
                                className="text-slate-400 hover:text-slate-700 text-xs"
                                aria-label="Close"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <p className="text-[10.5px] text-slate-500 mb-3 leading-snug">
                              Toggle any section on or off. Preferences save automatically and apply to every category you open.
                            </p>
                            {WIDGET_GROUPS.map((group, gi) => (
                              <div key={group.title} className={gi > 0 ? "mt-3 pt-3 border-t border-slate-100" : ""}>
                                <div className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-1.5">
                                  {group.title}
                                </div>
                                {group.desc && (
                                  <p className="text-[10px] text-slate-400 mb-1.5 leading-snug">{group.desc}</p>
                                )}
                                <div className="space-y-0.5">
                                  {group.items.map(w => (
                                    <label
                                      key={w.key}
                                      className="flex items-center gap-2.5 p-1.5 rounded-lg hover:bg-slate-50 cursor-pointer"
                                      data-testid={`dd-control-panel-toggle-${w.key}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={!!widgetVis[w.key]}
                                        onChange={() => toggleWidget(w.key)}
                                        className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                      />
                                      <span className="text-xs font-medium text-slate-700 flex-1">{w.label}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    {/* PDF download — pulse-animated, anchored on the storyboard */}
                    <button
                      onClick={handleDownloadPdf}
                      disabled={downloading || loading || !data}
                      className={`group relative h-10 px-4 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-xs font-semibold inline-flex items-center gap-2 transition-all hover:scale-[1.03] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${downloading ? '' : 'dd-pdf-pulse'}`}
                      title="Download this deep dive as a PDF"
                      data-testid="dd-download-pdf-btn"
                    >
                      {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      <span className="tracking-wide">{downloading ? 'Generating…' : 'Download PDF'}</span>
                    </button>
                  </div>
                </div>
                <h1 className="text-4xl sm:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05] mb-3 flex items-baseline gap-3 flex-wrap">
                  <span>{isSingleBrand ? appliedBrands[0] : (displayName || category)}</span>
                  <span
                    className="inline-flex items-center gap-1.5 text-sm font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200 align-middle"
                    title={`Amazon ${mp.label}`}
                  >
                    <span className="text-base leading-none">{mp.flag}</span>
                    {mp.code}
                  </span>
                </h1>
                <p className="text-sm text-slate-500 font-mono tabular-nums">
                  {brandFiltered ? (
                    <>
                      {data.products} of {categoryTotal} {(displayName || category).toLowerCase()} products
                      {appliedBrands.length > 1 && (
                        <> · {appliedBrands.length} brands</>
                      )}
                      {' '}· {data.runs} runs · last {data.days} days
                    </>
                  ) : (
                    <>{data.products} products · {data.runs} runs · last {data.days} days</>
                  )}
                  {shareBasis === "units" && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 align-middle">
                      <Sparkles className="w-2.5 h-2.5" />
                      demand-weighted
                    </span>
                  )}
                </p>
                {brandFiltered && (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-500">scoped to</span>
                    {appliedBrands.map(b => (
                      <span key={b} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-100 text-violet-800 text-[11px] font-bold border border-violet-200">
                        {b}
                      </span>
                    ))}
                    <span className="text-[10px] text-slate-400 font-mono tabular-nums ml-1">
                      in {category}
                    </span>
                  </div>
                )}

                {/* Hero number grid — 3-column 2-row gives each number proper breathing room */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-10 mt-14 max-w-4xl">
                  <HeroStat
                    label="ASP"
                    value={fmtP(data.asp.avg)}
                    sub={data.asp.avg != null ? `med ${fmtP(data.asp.median)} · ${mp.symbol}${data.asp.min}–${data.asp.max}` : 'no price data'}
                    accent="text-indigo-700"
                  />
                  <HeroStat
                    label="Rating"
                    value={data.rating.avg ? data.rating.avg.toFixed(2) : "—"}
                    sub={data.rating.median ? `median ${data.rating.median.toFixed(1)}` : 'no rating data'}
                    accent="text-amber-700"
                  />
                  <HeroStat
                    label="Reviews"
                    value={fmtCount(data.total_reviews)}
                    sub="customer engagement"
                    accent="text-emerald-700"
                  />
                  {isSingleBrand ? (
                    <>
                      <HeroStat
                        label="SKUs"
                        value={data.products}
                        sub={categoryTotal ? `${((data.products / categoryTotal) * 100).toFixed(1)}% of category shelf` : 'in this category'}
                        accent="text-violet-700"
                      />
                      <HeroStat
                        label="Category Share"
                        value={categoryTotal ? `${((data.products / categoryTotal) * 100).toFixed(1)}%` : '—'}
                        sub={`of ${categoryTotal} ${category.toLowerCase()} bestsellers`}
                        accent="text-fuchsia-700"
                      />
                      <HeroStat
                        label="New"
                        value={data.new_entrants.length}
                        sub={data.new_entrants.length > 0 ? 'recent SKUs' : 'no recent launches'}
                        accent="text-rose-700"
                      />
                    </>
                  ) : (
                    <>
                      <HeroStat
                        label="Brands"
                        value={brandShare.length}
                        sub={brandShare[0] ? `${brandShare[0].brand} · ${brandShare[0].share_pct}%` : '—'}
                        accent="text-violet-700"
                      />
                  <div className="relative">
                    <HeroStat
                      label={
                        <span className="inline-flex items-center gap-1">
                          Top 3 Share
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setHhiOpen(o => !o); }}
                            className="w-3 h-3 rounded-full bg-slate-300 hover:bg-slate-500 text-white flex items-center justify-center transition-colors"
                            title="What is Top 3 Share?"
                            data-testid="dd-top3-info-btn"
                          >
                            <Info className="w-2 h-2" />
                          </button>
                        </span>
                      }
                      value={`${top3Pct}%`}
                      sub={<span className={top3Info.tone + ' font-semibold'}>{top3Info.label}</span>}
                      accent="text-slate-700"
                    />
                    {hhiOpen && (
                      <div
                        className="absolute z-30 top-full mt-2 left-0 w-72 rounded-xl bg-slate-900 text-white p-4 shadow-2xl text-[11px] leading-relaxed"
                        data-testid="dd-top3-tooltip"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1.5">Top 3 Brand Share</div>
                        <p className="text-slate-200">
                          Combined shelf share of the three biggest brands in this category. The higher the number, the fewer players control the shelf.
                        </p>
                        {top3Brands.length > 0 && (
                          <div className="mt-3 space-y-1 font-mono tabular-nums text-[10px]">
                            {top3Brands.map((b, i) => (
                              <div key={b.brand} className="flex items-center justify-between text-slate-300">
                                <span className="truncate pr-2">{i + 1}. {b.brand}</span>
                                <span className="text-white">{b.share_pct}%</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="mt-3 pt-2 space-y-1.5 font-mono tabular-nums text-[10px] border-t border-slate-800">
                          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400"></span><span className="w-20 text-slate-400">&lt; 40%</span><span className="text-emerald-300">Fragmented</span></div>
                          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-400"></span><span className="w-20 text-slate-400">40–70%</span><span className="text-amber-300">Balanced</span></div>
                          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-400"></span><span className="w-20 text-slate-400">&gt; 70%</span><span className="text-red-300">Concentrated</span></div>
                        </div>
                        <div className="mt-3 pt-2 border-t border-slate-800 text-slate-400 text-[10px]">
                          This category: <span className={`font-bold ${top3Info.tone}`}>{top3Pct}%</span> — {top3Info.label.toLowerCase()}.
                        </div>
                      </div>
                    )}
                  </div>
                  <HeroStat
                    label="New"
                    value={data.new_entrants.length}
                    sub="entrants vs prior runs"
                    accent="text-pink-700"
                  />
                    </>
                  )}
                </div>

                {/* Scroll cue */}
                <div className="mt-16 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  <span>Scroll to explore</span>
                  <ArrowDown className="w-3 h-3 animate-bounce" />
                </div>
              </div>
            </section>

            {/* ════════════════════════════════════════════════════════════ */}
            {/* MARKET STRUCTURE — indigo wash ──────────────────────────── */}
            {/* Skip for single-brand drill-down (the brand IS the entire     */}
            {/* filtered set, so "who owns the shelf?" is moot).               */}
            {/* ════════════════════════════════════════════════════════════ */}
            {!isSingleBrand && widgetVis.market_structure && (
            <StorySection
              id="dd-section-1"
              eyebrow="01 · Market Structure"
              title="Who owns the shelf?"
              blurb={
                <>
                  <span className="font-semibold text-slate-700">{brandShare.length} brands</span> compete in this category, but the{' '}
                  <span className="font-semibold text-slate-700">top 3 own {top3Pct}%</span> of the shelf — a{' '}
                  <span className={`font-semibold ${top3Info.tone}`}>{top3Info.label.toLowerCase()}</span>{' '}
                  market where {top3Info.narrative}
                  <span className="block mt-1 text-[11px] text-slate-400 font-mono">
                    {shareBasis === "units"
                      ? `Share calculated from units sold (Amazon's "bought in past month" badge${data.total_units ? ` · ~${fmtCount(data.total_units)} units` : ''}).`
                      : 'Share calculated from listing count on bestseller pages. Re-run with Apify engine to unlock demand-weighted share.'}
                  </span>
                </>
              }
              accent="indigo"
              bg="bg-gradient-to-b from-white via-indigo-50/30 to-white"
            >
              <Reveal delay={120}>
                <div className="rounded-2xl bg-white border border-indigo-100/50 shadow-sm overflow-hidden">
                  <BrandShareGrid
                    brands={brandShare}
                    onBrandClick={openBrandDrilldown}
                    onEditBrandAlias={(raw) => setAliasModal({ attribute: 'brand', rawValue: raw })}
                  />
                </div>
              </Reveal>

              {/* Rank Race — futuristic bump chart of top-10 movement over time */}
              {widgetVis.rank_race && (
              <Reveal delay={180}>
                <div className="mt-10">
                  <RankRace category={category} days={days} marketplace={marketplace} />
                </div>
              </Reveal>
              )}

              <Reveal delay={240}>
                {/* Top 3 — podium treatment */}
                {brandShare.length >= 1 && (
                  <div className="mt-10">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-indigo-500 mb-4">Top 3 brands</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {brandShare.slice(0, 3).map((b, idx) => {
                        const podium = [
                          { Icon: Crown,  iconBg: 'bg-amber-400 text-white shadow-lg shadow-amber-200',  ring: 'ring-amber-200',  num: 'text-amber-700',  bar: 'from-amber-400 to-amber-500',  label: 'Leader' },
                          { Icon: Medal,  iconBg: 'bg-slate-300 text-white shadow-md',                  ring: 'ring-slate-200',  num: 'text-slate-700',  bar: 'from-slate-300 to-slate-400', label: 'Runner-up' },
                          { Icon: Medal,  iconBg: 'bg-orange-300 text-white shadow-md',                 ring: 'ring-orange-100', num: 'text-orange-700', bar: 'from-orange-300 to-orange-400',label: 'Third' },
                        ][idx];
                        return (
                          <button
                            key={b.brand}
                            type="button"
                            onClick={() => openBrandDrilldown(b)}
                            className={`group relative rounded-2xl border bg-white p-5 ring-1 ${podium.ring} text-left cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400`}
                            title={`See all ${b.count} ${b.brand} listing${b.count === 1 ? '' : 's'}`}
                            data-testid={`dd-top3-brand-card-${b.brand}`}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className={`w-10 h-10 rounded-full ${podium.iconBg} flex items-center justify-center`}>
                                <podium.Icon className="w-5 h-5" />
                              </div>
                              <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-slate-400">{podium.label}</span>
                            </div>
                            <div className={`text-3xl font-bold ${podium.num} font-mono tabular-nums leading-tight`}>{b.share_pct}%</div>
                            <div className="text-sm font-bold text-slate-800 mt-1 truncate" title={b.brand}>{b.brand}</div>
                            <div className="text-[10px] text-slate-500 font-mono tabular-nums flex items-center justify-between">
                              <span>{b.count} listings</span>
                              <span className="opacity-0 group-hover:opacity-100 text-indigo-600 font-semibold tracking-wide transition-opacity">View →</span>
                            </div>
                            <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full bg-gradient-to-r ${podium.bar} transition-all duration-1000`} style={{ width: `${Math.min(100, b.share_pct * 5)}%` }} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Long-tail list — brands #4 onwards in a sleek list */}
                {brandShare.length > 3 && (
                  <div className="mt-10">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-indigo-500 mb-4">The rest of the field · #4 onwards</div>
                    <div className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
                      {brandShare.slice(3, 12).map((b, idx) => {
                        const max = brandShare[0].count;
                        return (
                          <button
                            key={b.brand}
                            type="button"
                            onClick={() => openBrandDrilldown(b)}
                            className="group w-full flex items-center gap-4 px-5 py-3 border-b border-slate-50 last:border-b-0 hover:bg-indigo-50/40 text-left cursor-pointer transition-colors focus:outline-none focus-visible:bg-indigo-50/60"
                            title={`See all ${b.count} ${b.brand} listing${b.count === 1 ? '' : 's'}`}
                            data-testid={`dd-longtail-brand-${b.brand}`}
                          >
                            <span className="font-mono text-slate-300 tabular-nums text-xs w-6">#{idx + 4}</span>
                            <span className="flex-1 truncate text-slate-700 font-semibold text-sm">{b.brand}</span>
                            <div className="w-32 h-1 bg-slate-100 rounded-full overflow-hidden hidden sm:block">
                              <div className="h-full bg-indigo-300" style={{ width: `${(b.count / max) * 100}%` }} />
                            </div>
                            <span className="font-mono text-slate-500 tabular-nums text-xs w-12 text-right">{b.count}</span>
                            <span className="font-mono text-indigo-600 tabular-nums text-xs w-14 text-right font-semibold">{b.share_pct}%</span>
                            <ArrowUpRight className="w-3 h-3 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all" />
                          </button>
                        );
                      })}
                      {brandShare.length > 12 && (
                        <div className="px-5 py-2.5 bg-slate-50/50 text-[10px] font-mono text-slate-400 text-center">
                          +{brandShare.length - 12} more brands in the long tail
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Reveal>

              {data.heatmap.brands.length >= 2 && data.heatmap.flavours.length >= 2 && (
                <Reveal delay={360}>
                  <div className="mt-12">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-indigo-500 mb-2">Brand × Flavour</div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">Who owns which flavour</h3>
                    <p className="text-xs text-slate-500 mb-6">Darker rows = brands offering more unique flavours in the top-10 slice. The pill next to each brand shows its variety count.</p>
                    <div className="p-6 rounded-2xl bg-white border border-indigo-100/50">
                      <Heatmap heatmapUnique={data.heatmap_unique} onCellClick={openBrandFlavourDrilldown} />
                    </div>
                  </div>
                </Reveal>
              )}
            </StorySection>
            )}

            {/* ════════════════════════════════════════════════════════════ */}
            {/* PRICING — emerald wash ──────────────────────────────────── */}
            {/* ════════════════════════════════════════════════════════════ */}
            {widgetVis.pricing_landscape && (
            <StorySection
              id="dd-section-2"
              eyebrow="02 · Pricing Landscape"
              title={data.asp.avg != null ? `Average sells at ${fmtP(data.asp.avg)}` : 'Pricing landscape'}
              blurb={data.asp.avg != null
                ? `Spread runs from ₹${data.asp.min} to ₹${data.asp.max}. The histogram below shows where most listings cluster — useful for spotting under-served price bands.`
                : 'No price data captured yet for this category.'}
              accent="emerald"
              bg="bg-gradient-to-b from-white via-emerald-50/30 to-white"
            >
              <Reveal delay={120}>
                <div className="rounded-2xl bg-white border border-emerald-100/50 shadow-sm p-6">
                  {data.price_bands.length === 0 ? (
                    <div className="text-xs text-slate-400 py-12 text-center">No price data.</div>
                  ) : (
                    <>
                      <div className="text-[10px] text-slate-400 mb-2 font-medium">
                        Click any bar to see every listing in that price band.
                      </div>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart
                          data={data.price_bands}
                          margin={{ top: 12, right: 12, bottom: 28, left: 4 }}
                          onClick={(e) => {
                            // Recharts fires this for background clicks too; only
                            // drill in when a specific bar was hit (activePayload set).
                            const pl = e && e.activePayload && e.activePayload[0];
                            if (pl && pl.payload) openPriceBandDrilldown(pl.payload);
                          }}
                        >
                          <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b", fontFamily: "ui-monospace, monospace" }} interval={0} angle={-25} textAnchor="end" height={55} />
                          <YAxis tick={{ fontSize: 11, fill: "#64748b", fontFamily: "ui-monospace, monospace" }} width={32} />
                          <Tooltip
                            cursor={{ fill: "#f1f5f9" }}
                            contentStyle={{ fontSize: 11, padding: 8, borderRadius: 8, border: "1px solid #e2e8f0" }}
                            formatter={(value) => [`${value} listing${value === 1 ? '' : 's'}`, "Count"]}
                            labelFormatter={(label) => `${label} · click to drill in`}
                          />
                          <Bar
                            dataKey="count"
                            fill="#10b981"
                            radius={[6, 6, 0, 0]}
                            animationDuration={900}
                            cursor="pointer"
                            onClick={openPriceBandDrilldown}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </>
                  )}
                </div>
              </Reveal>

              {((data.explorer && data.explorer.length > 0) || (data.scatter && data.scatter.length > 0)) && widgetVis.price_positioning && (
                <Reveal delay={200}>
                  <div className="mt-12">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-500 mb-2">Price Positioning · 2×2</div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">
                      {isSingleBrand ? `Where do ${appliedBrands[0]}'s SKUs sit?` : 'Where does each brand play?'}
                    </h3>
                    <p className="text-xs text-slate-500 mb-6">
                      {isSingleBrand
                        ? <>Each SKU placed against the {category.toLowerCase()} category's price quartiles — </>
                        : <>Top 5 brands per band, by demand. Bands derived from category price quartiles — </>}
                      <span className="font-semibold text-emerald-700"> Budget Friendly</span> &lt; Q1,
                      <span className="font-semibold text-rose-700"> Mid Value</span> Q1–Q2,
                      <span className="font-semibold text-slate-700"> Upper Mid</span> Q2–Q3,
                      <span className="font-semibold text-amber-700"> Premium</span> ≥ Q3.
                    </p>
                    <PriceQuadrant
                      products={data.explorer || data.scatter}
                      marketplace={marketplaceCode}
                      onZoneClick={openZoneDrilldown}
                      onEditPackAlias={(raw) => setAliasModal({ attribute: 'pack_size', rawValue: raw })}
                    />
                  </div>
                </Reveal>
              )}

              {data.pack_size_dist.length > 0 && widgetVis.pack_sizes && (
                <Reveal delay={240}>
                  <div className="mt-12">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-500 mb-2">Pack sizes</div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">{data.pack_size_dist.length} pack sizes captured</h3>
                    <p className="text-xs text-slate-500 mb-6">Click any card to see the listings · click the pencil ✏ to merge equivalent labels (e.g. "60 Capsules" → "60 Servings").</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {data.pack_size_dist.slice(0, 12).map(p => {
                        const max = Math.max(1, ...data.pack_size_dist.map(x => x.count));
                        const pct = (p.count / max) * 100;
                        return (
                          <div key={p.label} className="group relative rounded-xl bg-white border border-slate-100 hover:border-emerald-200 hover:shadow-md transition-all">
                            <button
                              type="button"
                              onClick={() => openPackDrilldown(p.label)}
                              className="w-full p-4 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-300 rounded-xl"
                              title={`See all ${p.count} listing${p.count === 1 ? '' : 's'} in ${p.label}`}
                              data-testid={`pack-dist-card-${p.label}`}
                            >
                              <div className="text-lg font-bold tabular-nums text-emerald-700 font-mono pr-7">{p.label}</div>
                              <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 transition-all duration-1000" style={{ width: `${pct}%` }} />
                              </div>
                              <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-500 font-mono tabular-nums">
                                <span>{p.count} listings</span>
                                <span className="opacity-0 group-hover:opacity-100 text-emerald-600 font-semibold tracking-wide transition-opacity">View →</span>
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setAliasModal({ attribute: 'pack_size', rawValue: p.label }); }}
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 w-6 h-6 rounded-md inline-flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-transparent hover:border-indigo-200 transition-all"
                              title={`Standardize "${p.label}" — map to a clean display label`}
                              data-testid={`pack-dist-alias-edit-${p.label}`}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Reveal>
              )}

              {/* ─── Diet Type & Specialty — futuristic dual-card section ─── */}
              {((data.diet_type_dist && data.diet_type_dist.length > 0) || (data.specialty_dist && data.specialty_dist.length > 0)) && widgetVis.audience_signal && (
                <Reveal delay={300}>
                  <div className="mt-12">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-cyan-400 mb-2">Audience Signal</div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">Who is this category built for?</h3>
                    <p className="text-xs text-slate-500 mb-6">Diet & specialty positioning across bestsellers — hover any tag to manage its display label.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {data.diet_type_dist && data.diet_type_dist.length > 0 && (
                        <FuturisticAttrCard
                          title="Diet Type"
                          subtitle="Vegetarian · Vegan · Keto · …"
                          dist={data.diet_type_dist}
                          icon={Leaf}
                          accent="cyan"
                          onEditAlias={(raw) => setAliasModal({ attribute: 'diet_type', rawValue: raw })}
                          onLabelClick={(raw) => openAttrDrilldown('diet_type', raw, 'cyan')}
                        />
                      )}
                      {data.specialty_dist && data.specialty_dist.length > 0 && (
                        <FuturisticAttrCard
                          title="Specialty"
                          subtitle="Sugar-Free · Gluten-Free · Organic · …"
                          dist={data.specialty_dist}
                          icon={ShieldCheck}
                          accent="fuchsia"
                          onEditAlias={(raw) => setAliasModal({ attribute: 'specialty', rawValue: raw })}
                          onLabelClick={(raw) => openAttrDrilldown('specialty', raw, 'fuchsia')}
                        />
                      )}
                    </div>
                  </div>
                </Reveal>
              )}

              {data.scatter.length > 0 && widgetVis.price_rating_reviews && (
                <Reveal delay={360}>
                  <div className="mt-12">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-500 mb-2">Price × Rating × Reviews</div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">Premium underperformers, hidden gems</h3>
                    <p className="text-xs text-slate-500 mb-6">Bubble size = review volume. Top-right = expensive & adored, bottom-right = expensive & overlooked.</p>
                    <div className="p-6 rounded-2xl bg-white border border-emerald-100/50">
                      <ScatterPlot scatter={data.scatter} />
                    </div>
                  </div>
                </Reveal>
              )}
            </StorySection>
            )}

            {/* ════════════════════════════════════════════════════════════ */}
            {/* DEMAND SIGNALS — amber wash ─────────────────────────────── */}
            {/* ════════════════════════════════════════════════════════════ */}
            {widgetVis.demand_signals && (
            <StorySection
              id="dd-section-3"
              eyebrow="03 · Demand Signals"
              title={`${fmtCount(data.total_reviews)} customers spoke up`}
              blurb={`Review count is the cleanest proxy for actual units sold. Below are the products with the strongest customer engagement in this category, plus the spread of ratings.`}
              accent="amber"
              bg="bg-gradient-to-b from-white via-amber-50/30 to-white"
            >
              <Reveal delay={120}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="rounded-2xl bg-white border border-amber-100/50 p-6">
                    <div className="flex items-center gap-2 mb-5">
                      <Star className="w-4 h-4 text-amber-500" />
                      <h3 className="text-sm font-bold text-slate-800">Rating distribution</h3>
                    </div>
                    <RatingDistBar distribution={data.rating.distribution} />
                  </div>

                  <div className="rounded-2xl bg-white border border-amber-100/50 p-6">
                    <div className="flex items-center gap-2 mb-5">
                      <MessageSquare className="w-4 h-4 text-amber-500" />
                      <h3 className="text-sm font-bold text-slate-800">Top reviewed</h3>
                    </div>
                    {data.top_reviewed.length === 0 ? (
                      <div className="text-xs text-slate-400 py-6 text-center">No review data captured.</div>
                    ) : (
                      <div className="space-y-1.5">
                        {data.top_reviewed.slice(0, 7).map(p => (
                          <a key={p.asin} href={p.url} target="_blank" rel="noreferrer" className="group flex items-start gap-2.5 px-2 py-2 rounded-lg hover:bg-amber-50/50 transition-colors">
                            {p.image && <img src={p.image} alt="" className="w-9 h-9 rounded object-cover bg-slate-100 shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <div className="text-[11.5px] text-slate-800 font-medium leading-snug line-clamp-2 break-words" title={p.title}>{p.title}</div>
                              <div className="mt-1 text-[10px] text-slate-500 flex items-center gap-2 font-mono tabular-nums flex-wrap">
                                <span className="truncate max-w-[90px]" title={p.brand}>{p.brand || "—"}</span>
                                <span className="text-amber-500">★{p.rating || "—"}</span>
                                <span className="text-emerald-600 font-semibold">{fmtCount(p.reviews_count)}</span>
                                {p.price && <span>₹{p.price}</span>}
                              </div>
                            </div>
                            <ArrowUpRight className="w-3 h-3 text-slate-300 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            </StorySection>
            )}

            {/* ════════════════════════════════════════════════════════════ */}
            {/* MOVEMENT — pink wash (only if new entrants) ─────────────── */}
            {/* ════════════════════════════════════════════════════════════ */}
            {widgetVis.movement && data.new_entrants.length > 0 && (
              <StorySection
                id="dd-section-4"
                eyebrow="04 · Movement"
                title={`${data.new_entrants.length} new entrant${data.new_entrants.length !== 1 ? 's' : ''} this run`}
                blurb="ASINs that surfaced only in the latest run — early signal for what's gaining shelf space."
                accent="pink"
                bg="bg-gradient-to-b from-white via-pink-50/30 to-white"
              >
                <Reveal delay={120}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {data.new_entrants.map(p => (
                      <a key={p.asin} href={p.url} target="_blank" rel="noreferrer" className="group flex items-start gap-3 p-3 rounded-xl border border-pink-100/60 bg-white hover:bg-pink-50/40 hover:border-pink-200 transition-colors">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-pink-500 text-white text-[10px] font-bold tabular-nums shrink-0">#{p.rank || "?"}</span>
                        {p.image && <img src={p.image} alt="" className="w-9 h-9 rounded object-cover bg-slate-100 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-[11.5px] text-slate-800 font-medium leading-snug line-clamp-2 break-words" title={p.title}>{p.title}</div>
                          <div className="mt-1 text-[10px] text-slate-500 font-mono tabular-nums flex items-center gap-2 flex-wrap">
                            <span className="truncate max-w-[100px]" title={p.brand}>{p.brand || "—"}</span>
                            {p.price && <span>₹{p.price}</span>}
                            {p.rating && <span className="text-amber-500">★{p.rating}</span>}
                          </div>
                        </div>
                        <ArrowUpRight className="w-3 h-3 text-pink-300 group-hover:text-pink-600 shrink-0 mt-1" />
                      </a>
                    ))}
                  </div>
                </Reveal>
              </StorySection>
            )}

            {/* ════════════════════════════════════════════════════════════ */}
            {/* EXPLORER — violet wash, the deep dive table ─────────────── */}
            {/* ════════════════════════════════════════════════════════════ */}
            {widgetVis.product_explorer && (
            <StorySection
              id="dd-section-5"
              eyebrow={`${data.new_entrants.length > 0 ? '05' : '04'} · Product Explorer`}
              title="Every ASIN in this category"
              blurb={`Search, sort by any column, click any ASIN to open it on Amazon. ${data.products} products total.`}
              accent="violet"
              bg="bg-gradient-to-b from-white via-violet-50/20 to-slate-50"
            >
              <Reveal delay={120}>
                <ProductExplorer
                  products={data.explorer}
                  category={category}
                  onEditBrandAlias={(raw) => setAliasModal({ attribute: 'brand', rawValue: raw, stage: true })}
                  onEditAlias={(attribute, raw) => setAliasModal({ attribute, rawValue: raw, stage: true })}
                  onAliasesUpdated={refetchAfterAlias}
                  onStageManual={stageManualChange}
                />
              </Reveal>
            </StorySection>
            )}

            {/* Footer */}
            <div className="px-12 py-8 bg-slate-50 border-t border-slate-100">
              <div className="max-w-6xl mx-auto flex items-center justify-between text-[10px] font-mono tabular-nums text-slate-400">
                <span>HORIZON · Category Intelligence</span>
                <span>{(displayName || category)} · {data.products} products · {data.runs} runs · {data.days}d</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right-edge section-jump rail — only rendered when deep-dive data has loaded */}
      {data && !loading && <SectionNav />}

      {/* Floating "Save changes" pill — appears when there are unsaved */}
      {/* alias or manual attribute edits. Commits everything in one bulk */}
      {/* round-trip so the UI never re-renders per edit. */}
      {pendingChanges.length > 0 && (
        <div
          className="fixed bottom-6 right-6 z-40 flex flex-col items-stretch gap-2 bg-white/95 backdrop-blur-md border border-indigo-200 rounded-2xl shadow-2xl p-2 max-w-sm"
          data-testid="dd-pending-pill"
        >
          <div className="flex items-center gap-2 px-1">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-60" />
              <span className="relative rounded-full w-2 h-2 bg-amber-500" />
            </span>
            <span className="text-[11px] font-semibold text-slate-700 tabular-nums flex-1" data-testid="dd-pending-count">
              {pendingChanges.length} unsaved change{pendingChanges.length === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              onClick={() => setPendingExpanded(v => !v)}
              className="w-6 h-6 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
              title={pendingExpanded ? "Hide staged edits" : "Show staged edits"}
              data-testid="dd-pending-toggle"
            >
              {pendingExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
            </button>
          </div>
          {pendingExpanded && (
            <div className="max-h-72 overflow-y-auto border-t border-slate-100 pt-1" data-testid="dd-pending-tray">
              {pendingChanges.map((c, i) => (
                <div
                  key={c.id}
                  className="group flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors text-[10.5px]"
                  data-testid={`dd-pending-item-${i}`}
                >
                  <span className={`shrink-0 inline-flex items-center justify-center mt-0.5 w-4 h-4 rounded-full text-[8px] font-bold ${c.kind === "alias" ? "bg-indigo-100 text-indigo-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {c.kind === "alias" ? "A" : "M"}
                  </span>
                  <div className="flex-1 min-w-0">
                    {c.kind === "alias" ? (
                      <>
                        <div className="text-slate-700">
                          <span className="font-semibold uppercase text-[9px] tracking-wider text-slate-400">{c.attribute}</span>
                          <span className="font-mono mx-1 text-slate-500 truncate">"{c.raw_value}"</span>
                          <span className="text-slate-400">→</span>
                          <span className="font-mono ml-1 text-indigo-600 font-semibold truncate">"{c.display_value}"</span>
                        </div>
                        <div className="text-[9px] text-slate-400 mt-0.5">
                          scope: {c.category ? `only in ${c.category}` : "global"}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-slate-700">
                          <span className="font-semibold uppercase text-[9px] tracking-wider text-slate-400">{c.attribute}</span>
                          <span className="font-mono ml-1 text-emerald-700 font-semibold truncate">"{c.value}"</span>
                        </div>
                        <div className="text-[9px] text-slate-400 mt-0.5 font-mono">
                          ASIN: {c.asin}
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setPendingChanges(prev => prev.filter(x => x.id !== c.id))}
                    className="opacity-0 group-hover:opacity-100 shrink-0 w-5 h-5 inline-flex items-center justify-center rounded-full text-slate-300 hover:text-rose-500 hover:bg-rose-50 transition-all"
                    title="Remove this edit from the queue"
                    data-testid={`dd-pending-remove-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
            <button
              type="button"
              onClick={discardPending}
              disabled={savingBatch}
              className="flex-1 px-3 h-8 rounded-lg text-[11px] font-semibold text-slate-500 hover:bg-slate-100 disabled:opacity-50 transition-colors"
              data-testid="dd-pending-discard"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={saveAllPending}
              disabled={savingBatch}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 h-8 rounded-lg text-[11px] font-bold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-md hover:shadow-lg disabled:opacity-70 transition-all"
              data-testid="dd-pending-save"
            >
              {savingBatch
                ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
                : <><Save className="w-3 h-3" /> Save all</>}
            </button>
          </div>
        </div>
      )}

      {/* Drilldown ASIN modal — opens on brand-tile / quadrant-zone click */}
      {drilldown && (
        <DrilldownAsinModal
          title={drilldown.title}
          subtitle={drilldown.subtitle}
          products={drilldown.products}
          accent={drilldown.accent}
          marketplace={marketplaceCode}
          onClose={() => setDrilldown(null)}
        />
      )}

      {/* Brand-alias quick edit — invoked from two paths:
          1. Brand tiles / pricing cards (`stage: false`) — save IMMEDIATELY
             with a page refetch so the aggregated dashboard reflects the
             new display value on the next tick.
          2. Product Explorer pencil (`stage: true`) — queue the change
             into `pendingChanges` so the analyst can batch multiple edits
             and commit them all via the floating "Save all" pill. Avoids
             the per-edit page refresh users complained about.
          Bulk CSV upload and heatmap "Save all" also use `stageAliasChange`
          internally. */}
      {aliasModal && (
        <BrandAliasModal
          attribute={aliasModal.attribute}
          rawValue={aliasModal.rawValue}
          category={category}
          onClose={() => setAliasModal(null)}
          onStage={aliasModal.stage ? stageAliasChange : undefined}
          onSaved={async () => {
            setAliasModal(null);
            // When staging, the optimistic update inside stageAliasChange
            // already updated `data.explorer` — refetching now would clobber
            // every other staged edit still in the queue. Just close.
            if (aliasModal.stage) return;
            await refetchAfterAlias();
          }}
        />
      )}
    </div>
  );
};

// ─── DrilldownAsinModal ────────────────────────────────────────────
// Compact slide-over list of products for a brand or price-quadrant
// zone click. Uses the same accent palette as the source card.
const DRILL_ACCENTS = {
  indigo:  { ring: "ring-indigo-200",  text: "text-indigo-700",  bg: "bg-indigo-50",  bar: "bg-indigo-500" },
  amber:   { ring: "ring-amber-200",   text: "text-amber-700",   bg: "bg-amber-50",   bar: "bg-amber-500" },
  emerald: { ring: "ring-emerald-200", text: "text-emerald-700", bg: "bg-emerald-50", bar: "bg-emerald-500" },
  rose:    { ring: "ring-rose-200",    text: "text-rose-700",    bg: "bg-rose-50",    bar: "bg-rose-500" },
  slate:   { ring: "ring-slate-300",   text: "text-slate-700",   bg: "bg-slate-100",  bar: "bg-slate-500" },
};
const DrilldownAsinModal = ({ title, subtitle, products, accent = "indigo", marketplace, onClose }) => {
  const a = DRILL_ACCENTS[accent] || DRILL_ACCENTS.indigo;
  const fmtP = (n) => fmtMoney(n, marketplace || "IN");
  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="dd-drilldown-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100 ${a.bg}`}>
          <div className="min-w-0">
            <div className={`text-[10px] font-bold uppercase tracking-[0.25em] ${a.text} opacity-70`}>Drilldown</div>
            <div className="text-lg font-bold text-slate-900 truncate" title={title}>{title}</div>
            {subtitle && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{subtitle}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 w-8 h-8 rounded-full bg-white shadow-sm hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors"
            data-testid="dd-drilldown-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {products.length === 0 ? (
            <div className="text-xs text-slate-400 py-12 text-center">No products in this scope.</div>
          ) : (
            <div className="space-y-1.5">
              {products.map(p => (
                <a
                  key={p.asin}
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className={`group flex items-center gap-3 p-2.5 rounded-lg ring-1 ring-transparent hover:${a.ring} hover:bg-slate-50 transition-all`}
                  data-testid={`dd-drilldown-row-${p.asin}`}
                >
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md ${a.bar} text-white text-[10px] font-bold tabular-nums shrink-0`}>
                    #{p.rank || "?"}
                  </span>
                  {p.image && <img src={p.image} alt="" className="w-10 h-10 rounded object-cover bg-slate-100 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium text-slate-800 truncate" title={p.title}>{p.title || p.asin}</div>
                    <div className="text-[10px] text-slate-500 font-mono tabular-nums flex items-center gap-2 mt-0.5">
                      <span className="truncate max-w-[100px]" title={p.brand}>{p.brand || "—"}</span>
                      {p.price != null && <span>{fmtP(p.price)}</span>}
                      {p.rating != null && <span className="text-amber-500">★{Number(p.rating).toFixed(1)}</span>}
                      {p.pack_size && <span className="text-slate-400 truncate max-w-[80px]" title={p.pack_size}>{p.pack_size}</span>}
                    </div>
                  </div>
                  <ExternalLink className={`w-3.5 h-3.5 text-slate-300 group-hover:${a.text} shrink-0`} />
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-2.5 border-t border-slate-100 bg-slate-50 text-[10px] font-mono tabular-nums text-slate-500 flex items-center justify-between">
          <span>{products.length} product{products.length === 1 ? '' : 's'}</span>
          <span>Press Esc or click outside to close</span>
        </div>
      </div>
    </div>
  );
};

// ─── BrandAliasModal — tiny inline editor for brand standardization ────
const BrandAliasModal = ({ attribute, rawValue, category, onClose, onSaved, onStage }) => {
  const [raw, setRaw] = useState(rawValue || "");
  const [display, setDisplay] = useState(rawValue || "");
  // Default scope: when a category is open, keep the alias scoped to that
  // category so an edit in one deep-dive doesn't quietly rewrite the same
  // raw value across every other category too. User can still flip to
  // Global via the toggle. Falls back to global when no category context.
  const [scope, setScope] = useState(category ? "category" : "global");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const attrLabel = { brand: "Brand", flavour: "Flavour", item_form: "Item Form", pack_size: "Pack Size" }[attribute] || attribute;

  const handleSave = async () => {
    if (!raw.trim() || !display.trim()) { setError("Both fields required"); return; }
    setError(null);
    // When onStage is provided (batch-save mode), just queue the change
    // in the parent's pending list — no network call yet. The parent's
    // "Save changes" pill will flush every staged edit in one bulk POST.
    if (onStage) {
      onStage({
        attribute,
        raw_value: raw.trim(),
        display_value: display.trim(),
        category: scope === "category" ? (category || null) : null,
      });
      onSaved && onSaved();
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/attribute-aliases`, {
        attribute,
        raw_value: raw.trim(),
        display_value: display.trim(),
        category: scope === "category" ? (category || null) : null,
      });
      onSaved && onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="dd-brand-alias-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Pencil className="w-4 h-4 text-indigo-500" />
          <div>
            <div className="text-sm font-bold text-slate-900">Standardize value</div>
            <div className="text-[11px] text-slate-500">
              Map a messy scraped value to a clean display label.
            </div>
          </div>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
            <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{attrLabel}</span>
            {category && <span className="text-slate-400">· {category}</span>}
          </div>
          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Scraped value (raw)</label>
            <input
              type="text"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 font-mono"
              data-testid="dd-alias-raw-input"
            />
          </div>
          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Display as</label>
            <input
              type="text"
              value={display}
              onChange={(e) => setDisplay(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 font-semibold"
              data-testid="dd-alias-display-input"
            />
          </div>
          {category && (
            <div>
              <label className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Scope</label>
              <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
                <button type="button" onClick={() => setScope("global")} className={`px-3 py-1 text-xs font-semibold rounded transition-all ${scope === "global" ? "bg-white shadow-sm text-indigo-700" : "text-slate-500"}`}>Global</button>
                <button type="button" onClick={() => setScope("category")} className={`px-3 py-1 text-xs font-semibold rounded transition-all ${scope === "category" ? "bg-white shadow-sm text-indigo-700" : "text-slate-500"}`}>Only in {category}</button>
              </div>
            </div>
          )}
          {error && <div className="text-[11px] text-rose-600 font-medium">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-700 hover:bg-white transition-colors">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving || !raw.trim() || !display.trim()} className="px-4 py-2 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5" data-testid="dd-alias-save-btn">
            {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            {saving ? "Saving…" : (onStage ? "Stage change" : "Save mapping")}
          </button>
        </div>
      </div>
    </div>
  );
};

// Futuristic distribution card used for Diet Type & Specialty signals.
// Dark cyber-grid background, neon-accented bars, hover-revealed pencil
// for editing alias mappings — visually distinct from the rest of the
// emerald-themed deep dive so analysts spot the audience signal at a glance.
const FuturisticAttrCard = ({ title, subtitle, dist, icon: IconComp, accent = "cyan", onEditAlias, onLabelClick }) => {
  const total = dist.reduce((s, d) => s + (d.count || 0), 0);
  const top = dist[0] || null;
  const topShare = top && total > 0 ? Math.round((top.count / total) * 100) : 0;
  const palette = accent === "fuchsia"
    ? { glow: "#d946ef", ring: "ring-fuchsia-500/30", text: "text-fuchsia-300", bar: "from-fuchsia-500 to-purple-600", iconBg: "from-fuchsia-500/30 to-purple-600/30", chip: "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-400/30" }
    : { glow: "#22d3ee", ring: "ring-cyan-500/30", text: "text-cyan-300", bar: "from-cyan-500 to-blue-600", iconBg: "from-cyan-500/30 to-blue-600/30", chip: "bg-cyan-500/15 text-cyan-200 border-cyan-400/30" };

  return (
    <div className={`relative overflow-hidden rounded-2xl bg-slate-950 ring-1 ${palette.ring} shadow-[0_0_40px_rgba(0,0,0,0.4)] p-5`}>
      {/* Cyber grid background */}
      <div
        className="absolute inset-0 opacity-[0.18] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(${palette.glow}33 1px, transparent 1px), linear-gradient(90deg, ${palette.glow}33 1px, transparent 1px)`,
          backgroundSize: "26px 26px",
        }}
      />
      {/* Soft corner glow */}
      <div
        className="absolute -top-12 -right-12 w-44 h-44 rounded-full blur-3xl opacity-40 pointer-events-none"
        style={{ background: palette.glow }}
      />
      <div className="relative">
        <div className="flex items-start gap-3 mb-4">
          <div className={`shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${palette.iconBg} ring-1 ring-white/10 flex items-center justify-center backdrop-blur`}>
            <IconComp className={`w-4 h-4 ${palette.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40">{title}</div>
            <div className="text-base font-bold text-white mt-0.5">{subtitle}</div>
          </div>
          <span className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded border tabular-nums ${palette.chip}`}>
            {dist.length} {dist.length === 1 ? "tag" : "tags"}
          </span>
        </div>

        {top && (
          <button
            type="button"
            onClick={onLabelClick ? () => onLabelClick(top.label) : undefined}
            disabled={!onLabelClick}
            className={`w-full flex items-baseline gap-3 mb-4 rounded-lg px-2 py-1.5 -mx-2 text-left transition-colors ${onLabelClick ? 'hover:bg-white/5 cursor-pointer' : 'cursor-default'}`}
            title={onLabelClick ? `See ${top.count} product${top.count === 1 ? '' : 's'} with "${top.label}"` : undefined}
            data-testid={`futuristic-attr-top-${top.label}`}
          >
            <div className={`text-4xl font-extrabold tracking-tight ${palette.text} tabular-nums`}>{topShare}%</div>
            <div className="flex-1 min-w-0 text-white/70 truncate text-sm" title={top.label}>{top.label}</div>
            <div className="text-[10px] text-white/40 tabular-nums">{top.count}/{total} listings</div>
          </button>
        )}

        <div className="space-y-2">
          {dist.slice(0, 5).map((d) => {
            const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
            return (
              <div key={d.label} className="group flex items-center gap-2.5 text-xs">
                <button
                  type="button"
                  onClick={onLabelClick ? () => onLabelClick(d.label) : undefined}
                  disabled={!onLabelClick}
                  className={`flex-1 flex items-center gap-2.5 rounded-md px-1 py-0.5 text-left transition-colors ${onLabelClick ? 'hover:bg-white/5 cursor-pointer' : 'cursor-default'}`}
                  title={onLabelClick ? `See ${d.count} product${d.count === 1 ? '' : 's'} with "${d.label}"` : undefined}
                  data-testid={`futuristic-attr-row-${d.label}`}
                >
                  <div className="w-32 truncate text-white/80 font-medium" title={d.label}>{d.label}</div>
                  <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full bg-gradient-to-r ${palette.bar} transition-all duration-700`}
                      style={{ width: `${Math.max(pct, 3)}%`, boxShadow: `0 0 12px ${palette.glow}80` }}
                    />
                  </div>
                  <div className="w-12 text-right text-white/60 font-mono tabular-nums">{pct}%</div>
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onEditAlias && onEditAlias(d.label); }}
                  className="w-5 h-5 rounded opacity-0 group-hover:opacity-100 transition-all text-white/40 hover:text-white hover:bg-white/10 inline-flex items-center justify-center"
                  title={`Standardize "${d.label}"`}
                  data-testid={`futuristic-attr-edit-${d.label}`}
                >
                  <Pencil className="w-2.5 h-2.5" />
                </button>
              </div>
            );
          })}
          {dist.length > 5 && (
            <div className="text-[10px] text-white/30 pt-0.5">+{dist.length - 5} more</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryDeepDive;